import ApexCharts from "apexcharts";
import Chart from "react-apexcharts";
import React, {
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "bootstrap-daterangepicker/daterangepicker.css";
import CollapseHeader from "../../../core/common/collapse-header";
import { Deal, fetchAllDeals } from "../../../api/deal";
import { Note } from "../../../api/notes";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/interface";
import { Button, Container, Modal } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  fetchLeadStats,
  LeadCountsFunnel,
  LeadStageCount,
  LeadStageDetail,
  SeriesData,
  TargetDetail,
  timeAgo,
} from "../../../api/dashboard";
import {
  FaCheckCircle,
  FaPhone,
  FaUserClock,
  FaUserSlash,
} from "react-icons/fa";
import { FiTrendingDown } from "react-icons/fi";
import moment from "moment";
import { all_routes } from "../../router/all_routes";
import { fetchUsersByClientId, Todo, User } from "../../../api/todo";
import { CompanyResponse, fetchAllContacts } from "../../../api/contact";
import { fetchAllLeads, Lead } from "../../../api/lead";
import { highlightMatch } from "../../../core/common/highlightmatch";
import { ApexOptions } from "apexcharts";
import { useQuery } from "@tanstack/react-query";
import Loader from "../../../core/common/loader";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import { useNotes } from "../../../api/useNotes";
import { useTodos } from "../../../api/usetodo";
import { useCalendarEvents } from "../../../api/usecalendar";
import CountUp from "react-countup";
import ReactECharts from "echarts-for-react";
import Tip from "../../../core/common/tooltip";
import ChartExportControl from "../../../core/common/ChartExportControl";

const ReactApexChart = React.lazy(() => import("react-apexcharts"));
const DealsDashboard = () => {
  const route = all_routes;
  const navigate = useNavigate();

  const [funnelData, setFunnelData] = useState<LeadCountsFunnel>({
    total_leads: 0,
    not_contacted_leads: 0,
    closed_leads: 0,
    deals_won: 0,
    sortedData: [],
  });
  const [dealStatusSeries, setDealStatusSeries] = useState<SeriesData[]>([]);
  const [dealStatusLabels, setDealStatusLabels] = useState<string[]>([]);
  const [hoveredTodo, setHoveredTodo] = useState<number | null>(null);
  const [stages, setStages] = useState<LeadStageCount[]>([]);
  const [hoveredLegendIndex, setHoveredLegendIndex] = useState<number | null>(
    null
  );

  const max = Math.max(...stages.map((s) => s.total_leads || 0), 1);
  // Chart options for horizontal bar chart
  const dealStatusChartOptions: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: false,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "50%",
        dataLabels: {
          position: "top", // top of bar
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => val.toString(),
      style: {
        colors: ["#333"],
      },
    },
    xaxis: {
      categories: dealStatusLabels, // ✅ Labels: ["Open", "Closed", "Won", "Lost"]
      title: {
        text: "Count",
      },
    },
    yaxis: {
      title: {
        text: "Status",
      },
    },
    colors: ["#3B82F6", "#F59E0B", "#10B981", "#EF4444"],
    legend: {
      position: "bottom", // ✅ literal string
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} deals`,
      },
    },
  };
  //const [notes, setNotes] = useState<Note[]>([]);
  const [noteDescription, setNoteDescription] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [productRevenueData, setProductRevenueData] = useState<
    { name: string;[status: string]: number | string }[]
  >([]);
  const [statusDetails, setStatusDetails] = useState<
    { status: string; count: number }[]
  >([]);
  const clientId = useSelector((state: RootState) => state.auth.client_id);
  const bgColors = ["#E9EAFE", "#FFF1DA", "#D6F5E8", "#FFE9E9"];
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredContactIndex, setHoveredContactIndex] = useState<number | null>(
    null
  );
  //const [users, setUsers] = useState<User[]>([]);
  const [allLeads, setAllLeads] = useState<Lead[]>([]); // Renamed for clarity
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const COLORS = ["#f5222d", "#fa8c16", "#1890ff", "#d9d9d9"];
  const LEAD_STATUS_ORDER = ["Contacted", "Not_Contacted", "Closed", "Lost"];
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const [chartConfig, setChartConfig] = useState<any>({
    series: [],
    options: {},
  });
  const ITEMS_PER_PAGE = 5;
  const [tableData, setTableData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const getSourceWiseData = (deals: Deal[]) => {
    const sourceCountMap: { [key: string]: number } = {};
    deals.forEach((deal) => {
      const source = deal.dealsource?.deal_source_nema ?? "Unknown";
      sourceCountMap[source] = (sourceCountMap[source] || 0) + 1;
    });
    const labels = Object.keys(sourceCountMap);
    const series = Object.values(sourceCountMap);
    return { labels, series };
  };
  const [showExplanation, setShowExplanation] = useState(false);
  const [contactSourceChart, setContactSourceChart] = useState<{
    series: number[];
    labels: string[];
  }>({
    series: [],
    labels: [],
  });
  const [chartOptions1, setChartOptions1] = useState<any>({
    series: [],
    options: {},
  });
  const [dealsByYearChart, setDealsByYearChart] = useState<ApexCharts | null>(
    null
  );
  const [dealsByYearOptions, setDealsByYearOptions] = useState<any>({
    series: [],
    options: {},
  });

  const [leadsByStageOptions, setLeadsByStageOptions] = useState<any>({
    series: [],
    options: {},
  });
  const [selectedRange, setSelectedRange] = useState<"3m" | "6m" | "12m">(
    "12m"
  );
  //const [todos, setTodos] = useState<Todo[]>([]);
  const [allDeals, setAllDeals] = useState<Deal[]>([]); // Renamed for clarity
  const [filterDays, setFilterDays] = useState(30);
  const [monthlyClosedData, setMonthlyClosedData] = useState<
    { month: number; year: number; closed_value: number }[]
  >([]);
  const [totalClosedValue, setTotalClosedValue] = useState<number>(0);
  const [conversionRate, setConversionRate] = useState<string>("0.00%");
  //const [leadStats, setLeadStats] = useState<LeadStats | null>(null);
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [leadsByStage, setLeadsByStage] = useState<LeadStageCount[]>([]);
  const [leadsByStageDetails, setLeadsByStageDetails] = useState<
    LeadStageDetail[]
  >([]);
  const [visibleSlices, setVisibleSlices] = useState([
    true,
    true,
    true,
    true,
    true,
  ]);
  // Tab state
  const [activeTab, setActiveTab] = useState<"overall" | "monthly">("overall");
  const [allContacts, setAllContacts] = useState<CompanyResponse[]>([]);
  //const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const dealsByYearRef = useRef(null);
  const location = useLocation();
  const [showLoader, setShowLoader] = useState(false);
  const userId = useSelector((state: RootState) => state.auth.user_id);
  const userName = useSelector((state: RootState) => state.auth.username);
  const [targetData, setTargetData] = useState<TargetDetail[]>([]);
  const [userNames, setUserNames] = useState<string[]>([]);
  const [selectedBar, setSelectedBar] = useState<{
    seriesIndex: number;
    dataPointIndex: number;
  } | null>(null);
  const [sortBy, setSortBy] = useState<"all" | "2x" | "5x">("all");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [activityFilter, setActivityFilter] = useState("all");
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const [hoveredDealIndex, setHoveredDealIndex] = useState<number | null>(null);
  const STATUS_COLORS: Record<string, string> = {
    Not_Contacted: "#4DA3FF", // Blue – fresh/unreached
    Contacted: "#FFC107", // Yellow – in progress
    Closed: "#4CAF50", // Green – success
    Lost: "#E41F07", // Red – failure
  };

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [leaderboardSearch, setLeaderboardSearch] = useState("");


  const [monthlyPage, setMonthlyPage] = useState(1);
  const monthlyRows = 5;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (idx: number) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };
  const EllipsisWithTooltip = ({ text }: { text: string }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      if (ref.current) {
        setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
      }
    }, [text]);

    const content = (
      <span
        ref={ref}
        className="text-truncate d-inline-block"
        style={{ maxWidth: "140px" }}
      >
        {text}
      </span>
    );

    return isTruncated ? <Tip text={text}>{content}</Tip> : content;
  };

  const closeInvoice = () => {
    setSelectedUser(null);
  };

  const chartRef = useRef(null);

  const handleOpenTodo = (todo: Todo) => {
    setSelectedTodo(todo);
    setShowTodoModal(true);
  };

  const handleCloseTodo = () => {
    setShowTodoModal(false);
    setSelectedTodo(null);
  };

  const noteColors = [
    "#f8d7da", // light red
    "#d4edda", // light green
    "#fff3cd", // light yellow
    "#e2e3f3", // light purple
    "#f5c6cb", // light pink
    "#d1ecf1", // light blue
  ];

  const getRandomNoteColor = (index: number) =>
    noteColors[index % noteColors.length];

  const topPerformers = useMemo(() => {
    const lower = searchTerm.trim().toLowerCase();

    let filtered = [...targetData];

    // 🔍 SEARCH by user name
    if (lower) {
      filtered = filtered.filter((u) =>
        u.user_name?.toLowerCase().includes(lower)
      );
    }

    // 🎯 INCENTIVE FILTER
    if (sortBy === "2x") {
      filtered = filtered.filter((u) => u.incentive_percentage === 2);
    } else if (sortBy === "5x") {
      filtered = filtered.filter((u) => u.incentive_percentage === 5);
    }

    // 🏆 SORT + TOP 5
    return filtered
      .sort(
        (a, b) => (b.calculated_incentive || 0) - (a.calculated_incentive || 0)
      )
      .slice(0, 5);
  }, [targetData, sortBy, searchTerm]);




  function numberToWords(num: number): string {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    if (num === 0) return "Zero";
    if (num < 20) return a[num];
    if (num < 100)
      return b[Math.floor(num / 10)] + (num % 10 ? " " + a[num % 10] : "");
    if (num < 1000)
      return (
        a[Math.floor(num / 100)] +
        " Hundred" +
        (num % 100 ? " " + numberToWords(num % 100) : "")
      );
    if (num < 100000)
      return (
        numberToWords(Math.floor(num / 1000)) +
        " Thousand" +
        (num % 1000 ? " " + numberToWords(num % 1000) : "")
      );
    if (num < 10000000)
      return (
        numberToWords(Math.floor(num / 100000)) +
        " Lakh" +
        (num % 100000 ? " " + numberToWords(num % 100000) : "")
      );
    return (
      numberToWords(Math.floor(num / 10000000)) +
      " Crore" +
      (num % 10000000 ? " " + numberToWords(num % 10000000) : "")
    );
  }

  const RevenueChart: ApexOptions = {
    chart: {
      height: 380,
      type: "bar",
      stacked: true,
      toolbar: { show: false },
      events: {
        dataPointSelection: (
          event,
          chartContext,
          { seriesIndex, dataPointIndex }
        ) => {
          setSelectedBar({ seriesIndex, dataPointIndex });
        },
      },
    },

    colors: [
      "#00B8D9",
      "#36B37E",
      "#FFAB00",
      "#FF5630",
      "#6554C0",
      "#2684FF",
      "#00C49F",
    ],

    plotOptions: {
      bar: {
        borderRadius: 5,
        horizontal: true,
        barHeight: "50%",
      },
    },

    states: {
      hover: { filter: { type: "lighten" } },
      active: { filter: { type: "darken" } },
    },

    xaxis: {
      categories: userNames,
      labels: {
        formatter: (val: any, opts: any) => {
          // Category label (username)
          if (opts?.w?.globals?.categoryLabels) {
            const username = opts.w.globals.categoryLabels[opts.dataPointIndex];
            if (username) return username;
          }

          // Numeric axis → format as ₹
          const num = Number(val);
          return isNaN(num) ? val : `₹${num.toLocaleString()}`;
        },
        style: { fontSize: "12px", colors: "#6B7280" },
      },
      title: {
        text: "Amount (₹)",
        style: { fontSize: "13px", color: "#6B7280" },
      },
    },

    // ⭐ UI CORRECT + TS-SAFE
    yaxis: {
      ...({ categories: userNames } as any),
      labels: {
        style: { fontSize: "13px", colors: "#6B7280" },
      },
    },

    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val: number, opts: any) {
          return opts.seriesName === "Incentive %"
            ? `${val}%`
            : `₹${val.toLocaleString()}`;
        },
      },
    },

    legend: {
      position: "top",
      horizontalAlign: "center",
      markers: { size: 10 },
      fontSize: "12px",
    },
  };

  const safeValue = (value: any) =>
    typeof value === "number" && !isNaN(value) ? value : 0;

  const RevenueSeries = [
    {
      name: "Salary",
      data: userNames.map((_, i) => safeValue(targetData[i]?.salary)),
    },
    {
      name: "Target",
      data: userNames.map((_, i) => safeValue(targetData[i]?.target)),
    },
    {
      name: "Incentive Target",
      data: userNames.map((_, i) => safeValue(targetData[i]?.incentive_target)),
    },
    {
      name: "Total Deal Value",
      data: userNames.map((_, i) => safeValue(targetData[i]?.total_deal_value)),
    },
    {
      name: "Incentive %",
      data: userNames.map((_, i) =>
        safeValue(targetData[i]?.incentive_percentage)
      ),
    },
    {
      name: "Calculated Incentive",
      data: userNames.map((_, i) =>
        safeValue(targetData[i]?.calculated_incentive)
      ),
    },
    {
      name: "Incentive Achieved Amount",
      data: userNames.map((_, i) =>
        safeValue(targetData[i]?.incentive_achieved_amount)
      ),
    },
  ];

  const toggleSlice = (index: number) => {
    setVisibleSlices((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };

  const [monthlyLeadDealStatsChart, setMonthlyLeadDealStatsChart] = useState<{
    options: ApexOptions;
    series: number[];
  }>({
    options: {
      chart: {
        height: 240,
        type: "donut",
        toolbar: { show: false },
        events: {
          dataPointSelection: (_e, _ctx, config) => {
            const labels = config?.w?.config?.labels ?? [];
            const clickedLabel = labels[config.dataPointIndex];

            if (!clickedLabel) return;

            switch (clickedLabel) {
              case "Total Contacts":
                navigate("/crm/contact-list");
                break;
              case "Total Leads":
                navigate("/leads");
                break;
              case "Deals Created":
                navigate("/crm/deals");
                break;
              case "Deals Won":
                navigate("/crm/deals?filter=Won");
                break;
            }
          },
        },
      },

      labels: [
        "Total Contacts",
        "Total Leads",
        "Deals Created",
        "Total Leads Closed",
        "Deals Won",
      ],

      colors: ["#1B84FF", "#FFC107", "#28C76F", "#6c757d", "#00B8D9"],

      legend: { show: false },

      plotOptions: {
        pie: {
          donut: {
            size: "55%",
            labels: {
              show: true,

              name: { show: true },

              value: {
                show: true,
                fontSize: "20px",
                fontWeight: 600,
                formatter: (val: string) => val,
              },

              total: {
                show: true,
                label: "Total",
                fontSize: "14px",
                formatter: (w: any): string => {
                  const totals: number[] = w?.globals?.seriesTotals ?? [];

                  const total = totals.reduce(
                    (sum: number, v: number) => sum + v,
                    0
                  );

                  return total.toString();
                },
              },
            },
          },
        },
      },

      stroke: {
        lineCap: "round",
        show: true,
        width: 0,
        colors: ["#fff"],
      },

      dataLabels: { enabled: false },
      tooltip: {
        custom: ({ seriesIndex, w }) => {
          const value = w.globals.series[seriesIndex];

          const totals: number[] = w.globals.seriesTotals || [];
          const total = totals.reduce(
            (sum: number, v: number) => sum + v,
            0
          );

          const pct = total ? ((value / total) * 100).toFixed(1) : "0.0";
          const label = w.config.labels[seriesIndex];
          const color = w.config.colors?.[seriesIndex] || "#999";

          return `
      <div style="
        padding: 8px 10px;
         background:${color};
        color: #fff;
        border-radius: 6px;
        font-size: 12px;
      ">
        <strong>${label}</strong>: ${value} (${pct}%)
      </div>
    `;
        },
      },

      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: { height: 200 },
            legend: { position: "bottom" },
          },
        },
      ],
    },

    // ✅ FIXED LENGTH — NEVER CHANGES
    series: [0, 0, 0, 0, 0],
  });

  const usersQuery = useQuery({
    queryKey: ["users", clientId],
    enabled: !!clientId,
    queryFn: () => fetchUsersByClientId(clientId),
    staleTime: 5 * 60 * 1000,
  });

  const statsQuery = useQuery({
    queryKey: ["stats", clientId],
    enabled: !!clientId,
    queryFn: ({ signal }) => fetchLeadStats({ signal }),
    staleTime: 5 * 60 * 1000,
  });

  const contactsQuery = useQuery({
    queryKey: ["contacts", clientId],
    enabled: !!clientId,
    queryFn: () => fetchAllContacts(),
    staleTime: 5 * 60 * 1000,
  });

  const leadsQuery = useQuery({
    queryKey: ["leads", clientId],
    enabled: !!clientId,
    queryFn: () => fetchAllLeads(),
    staleTime: 5 * 60 * 1000,
  });

  const dealsQuery = useQuery({
    queryKey: ["deals", clientId],
    enabled: !!clientId,
    queryFn: () => fetchAllDeals(),
    staleTime: 5 * 60 * 1000,
  });

  const isLoading =
    usersQuery.isLoading ||
    statsQuery.isLoading ||
    contactsQuery.isLoading ||
    leadsQuery.isLoading ||
    dealsQuery.isLoading;

  const isError =
    usersQuery.isError ||
    statsQuery.isError ||
    contactsQuery.isError ||
    leadsQuery.isError ||
    dealsQuery.isError;

  const dashboardData = useMemo(() => {
    // if (
    //   !usersQuery.data ||
    //   !statsQuery.data ||
    //   !contactsQuery.data ||
    //   !leadsQuery.data ||
    //   !dealsQuery.data
    // )
    //   return null;

    return {
      usersData: usersQuery.data,
      statsData: statsQuery.data,
      contactsData: contactsQuery.data,
      leadsData: leadsQuery.data,
      dealsData: dealsQuery.data,
    };
  }, [
    usersQuery.data,
    statsQuery.data,
    contactsQuery.data,
    leadsQuery.data,
    dealsQuery.data,
  ]);

  useEffect(() => {
    if (!dashboardData) return;
    startTransition(() => {
      const { usersData, statsData, contactsData, leadsData, dealsData } =
        dashboardData;
      setAllContacts(contactsData ?? []);
      setAllLeads(leadsData ?? []);
      setAllDeals(dealsData ?? []);
      if (!statsData) return;
      if (!usersData) return;

      const dealData = statsData.get_dealstatuscount?.reduce(
        (acc: any, curr: any) => ({ ...acc, ...curr }),
        {
          total_deal: 0,
          total_deals_open: 0,
          total_deals_closed: 0,
          total_deals_won: 0,
          total_deals_lost: 0,
        }
      ) || {
        total_deal: 0,
        total_deals_open: 0,
        total_deals_closed: 0,
        total_deals_won: 0,
        total_deals_lost: 0,
      };

      const totalLeads = dealData.total_deal || 0;
      const seriesData: SeriesData[] = [
        { name: "Total Leads", data: [totalLeads] },
        { name: "Open", data: [dealData.total_deals_open || 0] },
        { name: "Closed", data: [dealData.total_deals_closed || 0] },
        { name: "Won", data: [dealData.total_deals_won || 0] },
        { name: "Lost", data: [dealData.total_deals_lost || 0] },
      ];
      const labels = ["Status Counts"];
      setDealStatusSeries(seriesData);
      setDealStatusLabels(labels);

      const stageData = statsData.get_total_leads_by_all_stage || [];
      setStages(stageData);

      const funnelArrayRaw = statsData.get_lead_counts_funnel || [];
      const funnel = funnelArrayRaw.reduce(
        (acc: any, curr: any) => ({ ...acc, ...curr }),
        {}
      );
      const funnelTotalLeads = funnel.total_leads || 0;
      const funnelArray = [
        {
          name: `Total Leads: ${funnelTotalLeads}`,
          value: funnelTotalLeads,
          percentage: 100,
        },
        {
          name: `Not Contacted: ${funnel.not_contacted_leads || 0}`,
          value: funnel.not_contacted_leads || 0,
          percentage:
            ((funnel.not_contacted_leads || 0) / funnelTotalLeads) * 100,
        },
        {
          name: `Contacted: ${funnel.contacted_leads || 0}`,
          value: funnel.contacted_leads || 0,
          percentage: ((funnel.contacted_leads || 0) / funnelTotalLeads) * 100,
        },
        {
          name: `Closed Leads: ${funnel.closed_leads || 0}`,
          value: funnel.closed_leads || 0,
          percentage: ((funnel.closed_leads || 0) / funnelTotalLeads) * 100,
        },
        {
          name: `Deals Won: ${funnel.deals_won || 0}`,
          value: funnel.deals_won || 0,
          percentage: ((funnel.deals_won || 0) / funnelTotalLeads) * 100,
        },
      ].sort((a, b) => b.value - a.value);
      setFunnelData({
        total_leads: funnel.total_leads || 0,
        not_contacted_leads: funnel.not_contacted_leads || 0,
        contacted_leads: funnel.contacted_leads || 0,
        closed_leads: funnel.closed_leads || 0,
        deals_won: funnel.deals_won || 0,
        sortedData: funnelArray,
      });

      const rawTargets = statsData?.get_all_targets;
      const targets = Array.isArray(rawTargets)
        ? rawTargets
        : rawTargets?.data ?? [];
      setTargetData(targets);
      const nameMap = usersData.reduce((acc, user) => {
        acc[user.id] = user.name;
        return acc;
      }, {} as Record<string, string>);
      setUserNames(targets.map((t) => nameMap[t.user_id] || "Unknown"));

      const sourceData = statsData.total_leads_by_source || [];
      const donutLabels = sourceData.map((s) => s.leadsource_name);
      const donutSeries = sourceData.map((s) => s.total_leads);
      const donutDetailsData = sourceData.map((s) => ({
        label: s.leadsource_name,
        count: s.total_leads,
      }));
      setDonutChart((prev: any) => ({
        ...prev,
        labels: donutLabels,
        series: donutSeries,
      }));
      setDonutDetails(donutDetailsData);

      const statusData =
        statsData.overall_lead_value_by_status_WITH_COUNT || [];
      const radialLabels = statusData.map((s) => s.status);
      const radialSeries = statusData.map((s) => s.lead_count);
      const radialTotalLeads = radialSeries.reduce((sum, val) => sum + val, 0);
      const statusDetailsData = statusData.map((s) => ({
        status: s.status,
        count: s.lead_count,
      }));
      setStatusDetails(statusDetailsData);
      setRadialChart((prev: any) => ({
        ...prev,
        labels: radialLabels,
        series: radialSeries,
        plotOptions: {
          ...prev.plotOptions,
          radialBar: {
            ...prev.plotOptions.radialBar,
            dataLabels: {
              ...prev.plotOptions.radialBar.dataLabels,
              total: {
                ...prev.plotOptions.radialBar.dataLabels.total,
                formatter: () => radialTotalLeads,
              },
            },
          },
        },
      }));

      setConversionRate(
        statsData.lead_to_deal_conversion_rate?.conversion_rate_percent ||
        "0.00%"
      );

      setLeadsByStage(statsData.get_total_leads_by_all_stage || []);
      setLeadsByStageDetails(
        statsData.get_total_leads_by_all_stage_withdetails || []
      );

      const allLeadsStats = statsData.get_leads_by_status_full_details || [];
      const notContactedLeadsRaw =
        statsData.get_leads_by_status_full_details_not_cntacted || [];
      const notContactedIds = new Set(
        notContactedLeadsRaw.map((l: any) => l.lead_id)
      );
      const notContactedLeads = allLeadsStats
        .filter((l: any) => notContactedIds.has(l.lead_id))
        .map((l: any) => ({ ...l, status: "Not_Contacted" }));
      const contactedLeads = allLeadsStats
        .filter((l: any) => !notContactedIds.has(l.lead_id))
        .map((l: any) => ({ ...l, status: "Contacted" }));
      const allGrouped = [...contactedLeads, ...notContactedLeads];
      setTableData(allGrouped);
      setFilteredData(allGrouped);
    });
  }, [dashboardData]);

  useEffect(() => {
    if (!dashboardData?.statsData?.get_monthly_count_of_lead_and_deal) return;

    const stats = dashboardData.statsData.get_monthly_count_of_lead_and_deal;

    const fullSeries = [
      stats.contacts_created_this_month || 0,
      stats.leads_created_this_month || 0,
      stats.deals_created_this_month || 0,
      stats.total_leads_closed || 0,
      stats.deals_won_this_month || 0,
    ];

    setMonthlyLeadDealStatsChart((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        labels: [
          "Total Contacts",
          "Total Leads",
          "Deals Created",
          "Total Leads Closed",
          "Deals Won",
        ],
        colors: ["#1B84FF", "#FFC107", "#28C76F", "#6c757d", "#00B8D9"],
      },
      series: fullSeries, // ✅ ALWAYS length = 5
    }));
  }, [dashboardData]);

  const leadDealStatsList = [
    {
      label: "Total Contacts",
      value: monthlyLeadDealStatsChart.series[0] || 0,
      color: monthlyLeadDealStatsChart.options.colors?.[0] || "#1B84FF",
      tooltip: "Total contacts added this month.",
    },
    {
      label: "Total Leads",
      value: monthlyLeadDealStatsChart.series[1] || 0,
      color: monthlyLeadDealStatsChart.options.colors?.[1] || "#FFC107",
      tooltip: "Number of leads generated this month.",
    },
    {
      label: "Deals Created",
      value: monthlyLeadDealStatsChart.series[2] || 0,
      color: monthlyLeadDealStatsChart.options.colors?.[2] || "#28C76F",
      tooltip: "Deals initiated from leads this month.",
    },
    {
      label: "Total Leads Closed",
      value:
        dashboardData?.statsData?.get_monthly_count_of_lead_and_deal
          ?.total_leads_closed || 0,
      color: "#6c757d",
      tooltip: "All leads closed (won/lost) this month.",
    },
    {
      label: "Deals Won",
      value:
        dashboardData?.statsData?.get_monthly_count_of_lead_and_deal
          ?.deals_won_this_month || 0,
      color: "#28C76F",
      tooltip: "Deals successfully closed this month.",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const scrolled = (scrollTop / docHeight) * 100;
      setScrollProgress(scrolled);

      const atBottom =
        window.innerHeight + scrollTop >= document.body.offsetHeight - 50;
      setShowScrollTop(atBottom);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const {
    notesQuery,
    createNoteMutation,
    updateNoteMutation,
    deleteNoteMutation,
  } = useNotes(userId);

  const notes = notesQuery.data ?? [];

  const { todosQuery } = useTodos(clientId);
  const todos = useMemo(() => {
    if (!todosQuery.data) return [];
    const all = todosQuery.data;

    const unique = new Map();
    all.forEach((todo: any) => {
      if (todo.todo_id) unique.set(todo.todo_id, todo);
    });

    return Array.from(unique.values())
      .sort(
        (a: any, b: any) =>
          moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
      )
      .slice(0, 10);
  }, [todosQuery.data]);

  const { data: calendarEvents } = useCalendarEvents();
  const events = useMemo(() => {
    if (!calendarEvents) return [];
    return calendarEvents
      .filter((e) =>
        moment(e.event_start).isSameOrAfter(moment().startOf("day"))
      )
      .sort(
        (a, b) =>
          moment(a.event_start).valueOf() - moment(b.event_start).valueOf()
      );
  }, [calendarEvents]);

  const handleViewContact = (contact: CompanyResponse["company"]) => {
    navigate(
      `/crm/contact-list?name=${encodeURIComponent(
        contact.contact_person_name
      )}`
    );
  };
  const handleViewDeal = (deal: Deal) => {
    navigate(`/crm/deals?deal_id=${encodeURIComponent(deal.deal_id)}`);
  };

  const openModal = (note?: Note) => {
    if (note) {
      setSelectedNote(note);
      setNoteDescription(note.notes_decription);
    } else {
      setSelectedNote(null);
      setNoteDescription("");
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNote(null);
    setNoteDescription("");
  };

  const saveNote = (selectedNote: Note | null, noteDescription: string) => {
    if (selectedNote) {
      updateNoteMutation.mutate(
        { id: selectedNote.note_id, text: noteDescription },
        {
          onSuccess: () => {
            closeModal();
          },
        }
      );
    } else {
      createNoteMutation.mutate(noteDescription, {
        onSuccess: () => {
          closeModal();
        },
      });
    }
  };

  const handleConfirmDelete = (noteId: string) => {
    setSelectedNoteId(noteId);
  };

  const confirmDeleteNote = (noteId: string | null) => {
    if (!noteId) return;

    deleteNoteMutation.mutate(noteId, {
      onSuccess: () => {
        setSelectedNoteId(null);
      },
    });
  };


  // Days difference helper
  const getDaysDiff = (date: string | null | undefined) => {
    if (!date) return Infinity;
    const now = new Date();
    return Math.floor(
      (now.getTime() - new Date(date).getTime()) / (1000 * 60 * 60 * 24)
    );
  };

  // Pick activity date
  const getActivityDate = (lead: Lead) => lead.updated_at || lead.created_at;

  // Tooltip reason
  const getActivityReason = (lead: any) => {
    if (!lead.updated_at) return "Lead created";
    return [
      lead.lead_status ? `Status: ${lead.lead_status}` : "",
      lead?.leadstage?.LeadStage_name
        ? `Stage: ${lead.leadstage?.LeadStage_name}`
        : "",
      lead.leadowner_name ? `Owner: ${lead.leadowner_name}` : "",
    ]
      .filter(Boolean)
      .join(", ");
  };

  // Activity badges
  const getActivityBadge = (days: number) => {
    if (days <= 2) return <span className="badge bg-success">Fresh</span>;
    if (days <= 7)
      return <span className="badge bg-warning text-dark">Warm</span>;
    if (days <= 30)
      return (
        <span className="badge bg-orange" style={{ background: "#f39c12" }}>
          Cold
        </span>
      );
    return <span className="badge bg-danger">Stale</span>;
  };

  const toggleSort = () => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  useEffect(() => {
    if (location.pathname === "/dashboard/deals-dashboard") {
      setShowLoader(true);
      setTimeout(() => {
        setShowLoader(false);
      }, 2000);
    }
  }, [location.pathname]);

  const [donutChart, setDonutChart] = useState<ApexOptions>({
    chart: {
      height: 350,
      type: "donut",
      toolbar: { show: false },
      events: {
        dataPointSelection: (event: any, chartContext: any, config: any) => {
          const chartId = chartContext?.el?.id;
          if (!chartId) return;

          const clickedLabel = config.w.config.labels[config.dataPointIndex];

          if (chartId === "leadTypeChart") {
            navigate(`/leads?type=${encodeURIComponent(clickedLabel)}`);
          } else if (chartId === "leadSourceChart") {
            navigate(`/leads?source=${encodeURIComponent(clickedLabel)}`);
          }
        },
      },
    },

    labels: [],
    colors: [
      "#0092E4", // your existing blue
      "#0CA678", // your green
      "#E41F07", // your brand red
      "#4A00E5", // deep purple
      "#FFA201", // bright orange
      "#36B37E", // soft green
      "#2684FF", // soft blue
      "#FF5630", // coral
      "#FAB005", // warm yellow
      "#6554C0", // violet
      "#00C49F", // mint teal
      "#FF8F00", // deep amber
    ],
    legend: {
      position: "bottom",
      markers: { size: 8 },
    },

    dataLabels: {
      enabled: true,
      formatter: (_, opts: any) => {
        const total = opts.w.globals.seriesTotals.reduce(
          (a: number, b: number) => a + b,
          0
        );
        const val = opts.w.config.series[opts.seriesIndex];
        const percent = ((val / total) * 100).toFixed(1);
        return `${percent}%`;
      },
      style: { fontSize: "12px", fontWeight: 600 },
    },

    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            name: { offsetY: 10 },
            value: {
              show: true,
              fontSize: "16px",
              formatter: (val: string) => val,
            },
            total: {
              show: true,
              label: "Total",
              formatter: function (w: any) {
                return w.globals.seriesTotals.reduce(
                  (a: number, b: number) => a + b,
                  0
                );
              },
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { height: 260 },
          legend: { position: "bottom" },
        },
      },
    ],
  });

  const [donutDetails, setDonutDetails] = useState<
    { label: string; count: number }[]
  >([]);

  const [radialChart, setRadialChart] = useState<any>({
    series: [],
    labels: [],
    colors: [
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#A28BD4",
      "#FF6699",
      "#0092E4",
      "#E41F07",
      "#5CB85C",
      "#FFA201",
    ],

    plotOptions: {
      radialBar: {
        hollow: {
          size: "45%",
        },
        track: {
          background: "#f2f2f2",
          strokeWidth: "100%",
        },
        dataLabels: {
          name: {
            fontSize: "16px",
            offsetY: -10,
          },
          value: {
            fontSize: "18px",
            fontWeight: 600,
            formatter: (val: any) => `${val}%`,
          },
          total: {
            show: true,
            label: "Total",
            formatter: (w: any) => {
              const sum = w.globals.seriesTotals.reduce(
                (acc: number, n: number) => acc + n,
                0
              );
              return `${sum}%`;
            },
          },
        },
      },
    },
  });

  const [sLineArea, setSLineArea] = useState<ApexOptions>({
    chart: {
      height: 350,
      type: "area",
      toolbar: {
        show: true,
        tools: {
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: "x", // horizontal zoom
        autoScaleYaxis: true,
      },
    },
    colors: ["#0092E4"], // premium blue

    dataLabels: { enabled: false },

    stroke: {
      curve: "smooth",
      width: 3,
    },

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.4,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },

    title: {
      text: "Monthly Won Deal Value",
      align: "left",
      style: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#444",
      },
    },

    series: [
      {
        name: "Closed Value",
        data: [], // dynamically updated
      },
    ],

    xaxis: {
      type: "category",
      categories: [],
      labels: {
        rotate: -30,
        style: {
          fontSize: "11px",
          fontWeight: 600,
          colors: "#6B7280",
        },
      },
      title: {
        text: "Month",
        style: { fontSize: "12px", color: "#6B7280" },
      },
    },

    yaxis: {
      labels: {
        formatter: (val: number) => `₹ ${val.toLocaleString()}`,
        style: { fontSize: "12px", colors: "#6B7280" },
      },
      title: {
        text: "Amount (₹)",
        style: { fontSize: "12px", color: "#6B7280" },
      },
    },

    tooltip: {
      theme: "light",
      y: {
        formatter: (val: number) => `₹ ${val.toLocaleString()}`,
      },
      x: {
        formatter: (_: any, opts: any) => {
          const label = opts.w.globals.categoryLabels[opts.dataPointIndex];
          return label;
        },
      },
    },

    grid: {
      strokeDashArray: 4,
    },
  });

  const statusWidgets = dashboardData?.statsData
    ?.overall_lead_value_by_status_WITH_COUNT
    ? [
      {
        label: "Contacted",
        value:
          dashboardData?.statsData?.overall_lead_value_by_status_WITH_COUNT.find(
            (s) => s.status === "Contacted"
          )?.lead_count || 0,
        subtext: "Contacted Leads",
        color: "bg-warning-subtle",
        icon: <FaPhone className="text-warning fs-3" />,
      },
      {
        label: "Not_Contacted",
        value:
          dashboardData?.statsData?.overall_lead_value_by_status_WITH_COUNT.find(
            (s) => s.status === "Not_Contacted"
          )?.lead_count || 0,
        subtext: "Not Contacted leads",
        color: "bg-info-subtle",
        icon: <FaUserSlash className="text-info fs-3" />,
      },
      {
        label: "Closed",
        value:
          dashboardData?.statsData?.overall_lead_value_by_status_WITH_COUNT.find(
            (s) => s.status === "Closed"
          )?.lead_count || 0,
        subtext: "Closed Leads",
        color: "bg-success-subtle",
        icon: <FaCheckCircle className="text-success fs-3" />,
      },
      {
        label: "Lost",
        value:
          dashboardData?.statsData?.overall_lead_value_by_status_WITH_COUNT.find(
            (s) => s.status === "Lost"
          )?.lead_count || 0,
        subtext: "Lost Leads",
        color: "bg-danger-subtle",
        icon: <FiTrendingDown className="text-danger fs-3" />,
      },
      {
        label: "Unassigned Contacts",
        value:
          dashboardData?.statsData?.overall_lead_value_by_status_WITH_COUNT.find(
            (s) => s.status.toLowerCase() === "unassigned"
          )?.lead_count || 0,
        subtext: "Unassigned Contacts",
        color: "bg-secondary-subtle",
        icon: <FaUserClock className="text-secondary fs-3" />,
      },
    ]
    : [];

  const stageOwnerCounts = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    if (
      dashboardData?.statsData &&
      dashboardData?.statsData?.get_total_leads_by_all_stage_withdetails
    ) {
      dashboardData?.statsData?.get_total_leads_by_all_stage_withdetails.forEach(
        (detail) => {
          if (!map[detail.lead_stage_name]) map[detail.lead_stage_name] = {};
          map[detail.lead_stage_name][detail.lead_owner_name] =
            (map[detail.lead_stage_name][detail.lead_owner_name] || 0) + 1;
        }
      );
    }
    return map;
  }, [dashboardData]);

  const getUserLeads = (userId: string) =>
    allLeads.filter((l) => l.contact?.users?.id === userId);

  const groupBy = (items: Lead[], keyGetter: (lead: Lead) => string) => {
    return items.reduce((acc, lead) => {
      const key = keyGetter(lead);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  };

  const renderBarData = (leads: Lead[]) => {
    const statusSum = leads.reduce((acc, lead) => {
      const key = lead.lead_status;
      const value = parseFloat(lead.lead_value || "0");
      acc[key] = (acc[key] || 0) + value;
      return acc;
    }, {} as Record<string, number>);

    return LEAD_STATUS_ORDER.map((status) => ({
      name: status,
      value: statusSum[status] || 0,
    }));
  };

  const getUniqueLeadTypes = () => {
    const types = new Set(
      allLeads.map((l) => l.leadtype?.leadtype_name.toLowerCase() || "unknown")
    );
    return Array.from(types);
  };

  const renderPieData = (leads: Lead[]) => {
    const typeCount = groupBy(
      leads,
      (l) => l.leadtype?.leadtype_name.toLowerCase() || "unknown"
    );
    return getUniqueLeadTypes().map((type) => ({
      name: type,
      value: typeCount[type] || 0,
    }));
  };

  const getLeadStatusCounts = (leads: Lead[]) => {
    const statusCounts = groupBy(leads, (l) => l.lead_status);
    return LEAD_STATUS_ORDER.map((status) => ({
      status,
      count: statusCounts[status] || 0,
    }));
  };

  const getTotalRevenueClosed = (leads: Lead[]) => {
    return leads
      .filter((l) => l.lead_status === "Closed")
      .reduce((sum, lead) => sum + parseFloat(lead.lead_value || "0"), 0)
      .toFixed(2);
  };

  const filteredUsers = dashboardData?.usersData?.filter((u) =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPerformanceData = () => {
    return dashboardData?.usersData?.map((user) => {
      const userLeads = getUserLeads(user.id);
      const dealsCreated = userLeads.length;
      const dealsWon = userLeads.filter(
        (l) => l.lead_status === "Closed"
      ).length;
      const winRate =
        dealsCreated > 0 ? ((dealsWon / dealsCreated) * 100).toFixed(1) : "0";
      const revenue = parseFloat(getTotalRevenueClosed(userLeads)) || 0;

      const revenueClosed = revenue;

      return {
        salesperson: user.name,
        leadsHandled: dealsCreated,
        dealsCreated,
        dealsWon,
        winRate: `${winRate}%`,
        revenueClosed: `₹${revenueClosed.toLocaleString("en-IN")}`,
      };
    });
  };

  // All leads for a specific month
  const getUserLeadsByMonth = (userId: string, monthKey: string) => {
    return getUserLeads(userId).filter((lead) => {
      const d = new Date(lead.created_at);
      const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      return key === monthKey;
    });
  };

  // Available months for dropdown
  const getUserAvailableMonths = (userId: string) => {
    const months = new Set<string>();
    getUserLeads(userId).forEach((lead) => {
      const d = new Date(lead.created_at);
      months.add(`${d.getFullYear()}-${d.getMonth() + 1}`);
    });
    return Array.from(months).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );
  };

  const monthlyLeads = getUserLeadsByMonth(
    selectedUser?.id || "",
    selectedMonth
  );

  const monthlyConversionRate = (
    (monthlyLeads.filter((l) => l.lead_status === "Closed").length /
      monthlyLeads.length) *
    100 || 0
  ).toFixed(1);

  const leadTypeCounts = useMemo(() => {
    const counts: { [key: string]: number } = {};
    allLeads.forEach((lead) => {
      const name = lead.leadtype?.leadtype_name || "Unknown";
      counts[name] = (counts[name] || 0) + 1;
    });

    return Object.entries(counts).map(([name, value]) => ({
      name,
      value,
    }));
  }, [allLeads]);

  const leadTypeOptions = useMemo(() => {
    const COLORS = [
      "#0088FE",
      "#00C49F",
      "#FFBB28",
      "#FF8042",
      "#A28BD4",
      "#FF6699",
      "#0092E4",
      "#E41F07",
      "#5CB85C",
      "#FFA201",
    ];

    const total = leadTypeCounts.reduce((s, x) => s + (x.value || 0), 0);
    const series = leadTypeCounts.map((d) => d.value || 0);
    const labels = leadTypeCounts.map((d) => d.name || "");

    return {
      chart: {
        type: "donut" as const,
        toolbar: { show: false },
        events: {
          dataPointSelection: (_e: any, _ctx: any, config: any) => {
            const safeLabels = config?.w?.config?.labels || [];
            const label = safeLabels[config?.dataPointIndex];
            if (label) {
              navigate(`/leads?type=${encodeURIComponent(label)}`);
            }
          },
        },
      },

      labels,
      series,

      colors: COLORS.slice(0, leadTypeCounts.length),

      legend: {
        show: true,
        position: "bottom" as const,
        fontSize: "13px",
        itemMargin: { horizontal: 8, vertical: 4 },
        markers: { width: 10, height: 10, radius: 12 },
        labels: { colors: "#6c757d" },
      },

      dataLabels: {
        enabled: true,
        formatter: (_: any, opts: any) => {
          const safeSeries = opts?.w?.config?.series || [];
          const count = safeSeries[opts?.seriesIndex] || 0;
          const pct = total ? ((count / total) * 100).toFixed(1) : "0.0";
          return `${pct}%`;
        },
        style: {
          fontSize: "14px",
          fontWeight: "bold",
        },
      },

      tooltip: {
        y: {
          formatter: (value: number, opts: any) => {
            const safeLabels = opts?.w?.config?.labels || [];
            const label = safeLabels[opts?.seriesIndex] || "";
            const pct = total ? ((value / total) * 100).toFixed(1) : "0.0";
            return `${value} Leads (${pct}%)`;
          },
        },
      },

      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total",
                color: "#495057",
                fontSize: "15px",
                formatter: () => total,
              },
            },
          },
        },
      },

      stroke: {
        width: 1,
        colors: ["#fff"],
      },

      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: { height: 260 },
            legend: { fontSize: "11px" },
          },
        },
      ],
    };
  }, [leadTypeCounts, navigate]);

  const leadTypeSeries = useMemo(
    () => leadTypeCounts.map((d) => d.value),
    [leadTypeCounts]
  );

  const performanceData = useMemo(
    () => getPerformanceData() || [],
    [allLeads, dashboardData?.usersData]
  );

  const itemsPerPage = 5;
  const [pages, setPage] = useState(1);

  useEffect(() => {
    setPage(1); // reset page when dataset changes
  }, [performanceData]);

  const totalPages = Math.ceil(performanceData.length / itemsPerPage);

  const visibleData = performanceData.slice(
    (pages - 1) * itemsPerPage,
    pages * itemsPerPage
  );

  const [showAllSources, setShowAllSources] = useState(false);
  const visibleSources = showAllSources
    ? donutDetails
    : donutDetails.slice(0, 3);
  const [showAllClosed, setShowAllClosed] = useState(false);
  const visibleMonthlyClosed = showAllClosed
    ? monthlyClosedData
    : monthlyClosedData.slice(0, 3);

  const leadsByStageDetailed = useMemo(() => {
    return leadsByStage.map((stage) => {
      const details = leadsByStageDetails.filter(
        (item) => item.lead_stage_id === stage.lead_stage_id
      );

      return {
        ...stage,
        details,
      };
    });
  }, [leadsByStage, leadsByStageDetails]);

  useEffect(() => {
    if (!leadsByStage.length) return;

    const total = leadsByStage.reduce(
      (sum: number, s: any) => sum + s.total_leads,
      0
    );

    const chartData = leadsByStage.map((stage: any) => ({
      x: stage.lead_stage_name,
      y: stage.total_leads,
    }));

    const dynamicColors = leadsByStage.map((_: any, idx: number) => {
      if (hoveredLegendIndex === null) return "#00918E"; // normal
      return hoveredLegendIndex === idx ? "#00918E" : "#D9EFEE"; // highlight
    });

    setLeadsByStageOptions({
      series: [
        {
          name: "Leads",
          data: chartData,
        },
      ],
      options: {
        chart: {
          type: "bar",
          height: 275,
          toolbar: { show: false },

          events: {
            // ---- Typed event ----
            dataPointSelection: (
              _e: MouseEvent,
              _ctx: any,
              config: { dataPointIndex: number }
            ) => {
              const stage = leadsByStage[config.dataPointIndex];
              if (stage) {
                navigate(
                  `/leads?stage=${encodeURIComponent(stage.lead_stage_name)}`
                );
              }
            },

            // ---- Typed event ----
            legendClick: (_ctx: any, legendIndex: number) => {
              const stage = leadsByStage[legendIndex];
              if (stage) {
                navigate(
                  `/leads?stage=${encodeURIComponent(stage.lead_stage_name)}`
                );
              }
              return false; // block hiding series
            },

            // ---- Typed event ----
            legendMouseEnter: (_chart: any, legendIndex: number) => {
              setHoveredLegendIndex(legendIndex);
            },

            // ---- Typed event ----
            legendMouseLeave: () => {
              setHoveredLegendIndex(null);
            },
          },
        },

        legend: {
          show: true,
          onItemClick: { toggleDataSeries: false },
          onItemHover: { highlightDataSeries: false },
        },

        plotOptions: {
          bar: {
            borderRadius: 8,
            borderRadiusApplication: "around",
            columnWidth: "45%",
            distributed: true,
          },
        },

        colors: dynamicColors,

        tooltip: {
          y: {
            formatter: (val: number) => {
              const percent = total ? ((val / total) * 100).toFixed(1) : "0";
              return `${val} leads (${percent}%)`;
            },
          },
        },

        xaxis: {
          labels: {
            show: false,
            rotate: -30,
            style: { fontSize: "11px", fontWeight: 600 },
          },
        },

        yaxis: {
          min: 0,
          tickAmount: 5,
          labels: {
            formatter: (v: number) => Math.round(v),
          },
        },
      },
    });
  }, [leadsByStage, hoveredLegendIndex, navigate]);

  useEffect(() => {
    const lower = employeeSearch.trim().toLowerCase();

    let filtered = tableData
      .filter((lead) =>
        lead.company_name?.toLowerCase().includes(lower) ||
        lead.leadowner_name?.toLowerCase().includes(lower)
      )
      .filter((lead) => {
        const days = getDaysDiff(getActivityDate(lead));

        switch (activityFilter) {
          case "fresh":
            return days <= 2;
          case "warm":
            return days > 2 && days <= 7;
          case "cold":
            return days > 7 && days <= 30;
          case "stale":
            return days > 30;
          case "nofollow":
            return !lead.updated_at;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        const aTime = new Date(getActivityDate(a)).getTime();
        const bTime = new Date(getActivityDate(b)).getTime();
        return sortDirection === "desc" ? bTime - aTime : aTime - bTime;
      });

    setFilteredData(filtered);
    setCurrentPage(0); // reset page on search
  }, [employeeSearch, tableData, activityFilter, sortDirection]);


  useEffect(() => {
    const lower = searchTerm.toLowerCase();

    let filtered = tableData
      .filter(
        (lead) =>
          lead.company_name?.toLowerCase().includes(lower) ||
          lead.leadowner_name?.toLowerCase().includes(lower)
      )
      .filter((lead) => {
        const days = getDaysDiff(getActivityDate(lead));

        switch (activityFilter) {
          case "fresh":
            return days <= 2;
          case "warm":
            return days > 2 && days <= 7;
          case "cold":
            return days > 7 && days <= 30;
          case "stale":
            return days > 30;
          case "nofollow":
            return !lead.updated_at;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        const aTime = new Date(getActivityDate(a)).getTime();
        const bTime = new Date(getActivityDate(b)).getTime();
        return sortDirection === "desc" ? bTime - aTime : aTime - bTime;
      });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchTerm, tableData, activityFilter, sortDirection]);

  const offset = currentPage * ITEMS_PER_PAGE;
  const currentPageData = filteredData.slice(offset, offset + ITEMS_PER_PAGE);
  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const page = Math.max(modalCurrentPage || 1, 1); // ensure it's never < 1
  const totalLeads = selectedUser ? getUserLeads(selectedUser.id).length : 0;
  const start = totalLeads === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, totalLeads);

  const derivedContactSourceChart = useMemo(() => {
    const sourceMap: Record<string, number> = {};
    allContacts.forEach(({ company }) => {
      const source = company.leadsource_name || "Unknown";
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    return {
      series: Object.values(sourceMap),
      labels: Object.keys(sourceMap),
    };
  }, [allContacts]);

  useEffect(() => {
    setContactSourceChart(derivedContactSourceChart);
  }, [derivedContactSourceChart]);

  // NEW: Derive chartOptions1 from allContacts
  const derivedChartOptions1 = useMemo(() => {
    const monthMap: Record<number, number> = {};

    allContacts.forEach(({ company }) => {
      if (company.created_at) {
        const date = new Date(company.created_at);
        const month = date.getMonth();
        monthMap[month] = (monthMap[month] || 0) + 1;
      }
    });

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const currentMonth = new Date().getMonth();
    const data = months.map((_, index) => monthMap[index] || 0);

    return {
      series: [{ name: "Companies Added", data }],

      title: {
        text: "Companies Added Over Months",
        align: "left",
        style: { fontSize: "14px", fontWeight: 600, color: "#444" },
      },

      chart: {
        height: 300,
        type: "line",
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true,
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            reset: true,
          },
        },

        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 900,
          animateGradually: { enabled: true, delay: 150 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
      },

      stroke: {
        curve: "smooth",
        width: 4,
        colors: ["#0092E4"],
      },

      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0.5,
          opacityFrom: 0.65,
          opacityTo: 0.25,
          stops: [0, 90, 100],
        },
      },

      markers: {
        size: 6,
        colors: ["#FFA201"],
        strokeWidth: 2,
        strokeColors: "#fff",
        hover: { size: 9 },

        discrete: [
          {
            seriesIndex: 0,
            dataPointIndex: currentMonth,
            fillColor: "#FF5722",
            strokeColor: "#FF5722",
            size: 10,
            shape: "circle",
          },
        ],
      },

      xaxis: {
        categories: months,
        labels: {
          style: { fontSize: "12px", fontWeight: 600, colors: "#6B7280" },
        },
        title: {
          text: "Month",
          style: { fontSize: "12px", color: "#6B7280" },
        },
      },

      yaxis: {
        min: 0,
        tickAmount: 5,
        labels: {
          formatter: (val: number) => val.toString(),
          style: { fontSize: "12px", colors: "#6B7280" },
        },
        title: {
          text: "Companies",
          style: { fontSize: "12px", color: "#6B7280" },
        },
      },

      tooltip: {
        theme: "light",
        x: { formatter: (val: any) => `Month: ${val}` },
        y: { formatter: (val: number) => `${val} companies` },
        marker: { show: true },
      },

      grid: {
        strokeDashArray: 4,
        borderColor: "#E5E7EB",
      },

      legend: {
        position: "top",
        horizontalAlign: "left",
      },
    };
  }, [allContacts]);

  const recentContacts = useMemo(() => {
    const now = new Date();
    return allContacts.filter(({ company }) => {
      const date = new Date(company.created_at || "");
      const diff = (now.getTime() - date.getTime()) / (1000 * 3600 * 24);
      return diff <= filterDays;
    });
  }, [allContacts, filterDays]);

  useEffect(() => {
    setChartOptions1(derivedChartOptions1);
  }, [derivedChartOptions1]);

  const derivedDealsByYearOptions = useMemo(() => {
    const closedDeals = allDeals.filter((deal) =>
      ["won", "closed"].includes(deal.status?.toLowerCase())
    );

    const today = new Date();
    const monthsBack =
      selectedRange === "3m" ? 3 : selectedRange === "6m" ? 6 : 12;

    const monthLabels = [];
    const dealCounts = [];
    const dealValues = [];

    for (let i = monthsBack - 1; i >= 0; i--) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const label = monthDate.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });

      const monthDeals = closedDeals.filter((deal) => {
        const d = new Date(deal.created_at);
        return (
          d.getMonth() === monthDate.getMonth() &&
          d.getFullYear() === monthDate.getFullYear()
        );
      });

      const totalValue = monthDeals.reduce((sum, deal) => {
        const v = parseFloat(deal.deal_value || "0");
        return sum + (isNaN(v) ? 0 : v);
      }, 0);

      monthLabels.push(label);
      dealCounts.push(monthDeals.length);
      dealValues.push(totalValue);
    }

    return {
      series: [
        { name: "Deals Count", type: "column", data: dealCounts },
        { name: "Total Deal Value", type: "line", data: dealValues },
      ],
      options: {
        chart: {
          height: 300,
          type: "line",
          stacked: false,
          toolbar: { show: false },
        },

        stroke: {
          width: [0, 4], // bold line
          curve: "smooth",
        },

        markers: {
          size: 5,
          strokeWidth: 2,
          colors: ["#fff"],
          strokeColors: ["#0E9F6E"],
        },

        plotOptions: {
          bar: { columnWidth: "45%" },
        },

        colors: ["#E41F07", "#0E9F6E"],

        xaxis: { categories: monthLabels },

        yaxis: [
          { title: { text: "Deals Count" } },
          {
            opposite: true,
            title: { text: "Total Deal Value" },
            labels: {
              formatter: (val: any) => `₹${(val || 0).toLocaleString()}`,
            },
          },
        ],

        tooltip: {
          shared: true,
          intersect: false,
          y: [
            { formatter: (val: any) => `${val} deals` },
            { formatter: (val: any) => `₹${val.toLocaleString()}` },
          ],
        },

        legend: {
          position: "top",
          horizontalAlign: "left",
        },
      },
    };
  }, [allDeals, selectedRange]);

  useEffect(() => {
    setDealsByYearOptions(derivedDealsByYearOptions);
  }, [derivedDealsByYearOptions]);

  const derivedMonthlyClosedData = useMemo(() => {
    const closedDeals = allDeals.filter(
      (deal) => deal.status?.toLowerCase() === "won" //|| deal.status?.toLowerCase() === "closed"
    );

    const monthlyMap: Record<number, number> = {};

    closedDeals.forEach((deal) => {
      const createdDate = new Date(deal.created_at);
      const month = createdDate.getMonth();
      const value = parseFloat(deal.deal_value) || 0;

      monthlyMap[month] = (monthlyMap[month] || 0) + value;
    });

    const currentYear = new Date().getFullYear(); // Dynamic
    const allMonths = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      year: currentYear,
      closed_value: parseFloat((monthlyMap[i] || 0).toFixed(2)),
    }));

    const categories = allMonths.map((item) =>
      new Date(currentYear, item.month - 1).toLocaleString("default", {
        month: "short",
      })
    );

    const values = allMonths.map((item) => item.closed_value);
    const total = values.reduce((sum, val) => sum + val, 0);

    setTotalClosedValue(total);
    setSLineArea((prev: any) => ({
      ...prev,
      xaxis: { ...prev.xaxis, categories },
      series: [{ name: "Closed Value", data: values }],
    }));

    return allMonths;
  }, [allDeals]);

  useEffect(() => {
    setMonthlyClosedData(derivedMonthlyClosedData);
  }, [derivedMonthlyClosedData]);

  const derivedProductRevenueData = useMemo(() => {
    const revenueMap: Record<string, Record<string, number>> = {};

    allDeals.forEach((deal) => {
      const productName = deal.product?.product_name || "Unknown";
      const status = deal.status || "unknown";
      const value = parseFloat(deal.deal_value) || 0;

      if (!revenueMap[productName]) revenueMap[productName] = {};

      revenueMap[productName][status] =
        (revenueMap[productName][status] || 0) + value;
    });

    return Object.entries(revenueMap).map(([productName, statusMap]) => ({
      name: productName,
      ...statusMap,
    }));
  }, [allDeals]);

  useEffect(() => {
    setProductRevenueData(derivedProductRevenueData);
  }, [derivedProductRevenueData]);

  const handleLegendClick = (e: any) => {
    if (activeKey === e.value) {
      setActiveKey(null); // reset filter
    } else {
      setActiveKey(e.value); // show only clicked legend item
    }
  };

  const derivedChartConfig = useMemo(() => {
    const { labels, series } = getSourceWiseData(allDeals);

    const total = series.reduce((a: number, b: number) => a + b, 0);

    return {
      series,
      labels,
      chart: {
        height: 350,
        type: "donut",
      },
      colors: [
        "#0092E4",
        "#4A00E5",
        "#E41F07",
        "#FFA201",
        "#0CA678",
        "#FAB005",
      ],

      // ⭐ BEAUTIFUL DONUT CENTER
      plotOptions: {
        pie: {
          donut: {
            size: "55%",
            labels: {
              show: true,

              // HIDE NAME to avoid congestion
              name: {
                show: true,
              } as any, // <— IMPORTANT FIX

              value: {
                show: true,
                fontSize: "20px",
                fontWeight: 600,
                offsetY: 10, // <— NOW VALID
                formatter: ((val: string) => val) as any,
              } as any, // <— FIX

              total: {
                show: true,
                label: "Total",
                fontSize: "14px",
                offsetY: -10, // <— NOW VALID
                formatter: function (w: any) {
                  return w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0
                  );
                },
              } as any, // <— FIX
            },
          },
        },
      },

      // ⭐ CLEAN TOOLTIP (Count + Percent)
      tooltip: {
        y: {
          formatter: function (val: number, opts: any) {
            const percent = total ? ((val / total) * 100).toFixed(1) : "0";
            return `${val} deals (${percent}%)`;
          },
        },
      },

      // ⭐ IMPROVED LEGEND (Label + %)
      legend: {
        position: "bottom",
        fontSize: "13px",
        markers: { width: 10, height: 10 },
        formatter: function (label: string, opts: any) {
          const count = opts.w.globals.series[opts.seriesIndex];
          const percent = total ? ((count / total) * 100).toFixed(1) : "0";
          return `${label}: ${count} (${percent}%)`;
        },
      },

      dataLabels: { enabled: false },

      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: { height: 260 },
            legend: { fontSize: "11px" },
            plotOptions: {
              pie: { donut: { size: "65%" } },
            },
          },
        },
      ],
    };
  }, [allDeals]);

  useEffect(() => {
    setChartConfig(derivedChartConfig);
  }, [derivedChartConfig]);

  const filteredDeals = useMemo(() => {
    const now = new Date();
    return allDeals.filter((deal) => {
      const createdAt = new Date(deal.created_at);
      const diffInDays =
        (now.getTime() - createdAt.getTime()) / (1000 * 3600 * 24);
      return diffInDays <= filterDays;
    });
  }, [allDeals, filterDays]);

  const dealStatusSummary = useMemo(() => {
    const counts = { Won: 0, Lost: 0, Closed: 0, Open: 0 };

    allDeals.forEach((d) => {
      const status = (d.status || "").toLowerCase();

      if (status === "won") counts.Won++;
      else if (status === "lost") counts.Lost++;
      else if (status === "closed") counts.Closed++;
      else if (status === "open") counts.Open++;
    });

    return [
      { name: "Won", value: counts.Won, color: "#28C76F" },
      { name: "Lost", value: counts.Lost, color: "#EA5455" },
      { name: "Closed", value: counts.Closed, color: "#6C757D" },
      { name: "Open", value: counts.Open, color: "#1B84FF" },
    ];
  }, [allDeals]);

  // Calculate total for label threshold
  const totalDeals = dealStatusSummary.reduce(
    (sum, item) => sum + item.value,
    0
  );
  const roseOptions = useMemo(() => {
    const total = totalDeals || 1;

    return {
      backgroundColor: "transparent",

      tooltip: {
        trigger: "item",
        confine: false,
        backgroundColor: "rgba(0,0,0,0.85)",
        borderColor: "transparent",
        textStyle: { color: "#fff", fontSize: 12 },
        formatter: (params: any) => {
          const { name, value, percent } = params;
          return `
          <div style="padding:4px 2px;">
            <strong>${name}</strong><br/>
            ${value.toLocaleString()} deals<br/>
            ${percent.toFixed?.(1) || percent}% of total
          </div>
        `;
        },
      },

      legend: {
        show: true,
        top: 10,
        left: "center",
        itemWidth: 10,
        itemHeight: 10,
        icon: "circle",
        textStyle: { fontSize: 12 },
        formatter: (name: string) => {
          const item = dealStatusSummary.find((d) => d.name === name);
          return `${name} (${item?.value || 0})`;
        },
      },

      series: [
        {
          name: "Deal Status",
          type: "pie",
          roseType: "radius",
          radius: ["20%", "75%"],
          center: ["50%", "60%"],

          label: {
            show: true,
            formatter: "{b}\n{d}%",
            color: "#333",
            fontSize: 11,
            fontWeight: 500,
          },

          labelLine: {
            show: true,
            length: 10,
            length2: 8,
            smooth: 0.3,
          },

          itemStyle: {
            borderRadius: 6,
            borderColor: "#fff",
            borderWidth: 2,
          },

          emphasis: {
            scale: true,
            scaleSize: 10,
            itemStyle: {
              shadowBlur: 20,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.25)",
            },
          },

          data: dealStatusSummary.map((item) => ({
            value: item.value,
            name: item.name,
          })),
        },
      ],

      color: dealStatusSummary.map((d) => d.color),

      responsive: true,
    };
  }, [dealStatusSummary, totalDeals]);

  const handleChartClick = useCallback(
    (params: any) => {
      navigate(`/crm/deals?status=${encodeURIComponent(params.name)}`);
    },
    [navigate]
  );

  const sortedRecentDeals = useMemo(
    () =>
      filteredDeals.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [filteredDeals]
  );

  const recentDeals = useMemo(() => {
    const now = new Date();

    return sortedRecentDeals.filter((deal) => {
      const createdAt = deal?.created_at || deal?.created_at;
      if (!createdAt) return false;

      const date = new Date(createdAt);
      const diff = (now.getTime() - date.getTime()) / (1000 * 3600 * 24);
      return diff <= filterDays;
    });
  }, [sortedRecentDeals, filterDays]);

  // ========== BASE USER DATA ==========
  const userLeads = selectedUser ? getUserLeads(selectedUser.id) || [] : [];
  const closedLeads = userLeads.filter((l) => l.lead_status === "Closed");
  const totalLeads1 = userLeads.length;
  const totalClosed = closedLeads.length;
  const totalRevenueClosed = getTotalRevenueClosed(userLeads);
  const conversionRate1 =
    totalLeads1 > 0 ? ((totalClosed / totalLeads1) * 100).toFixed(1) : "0.0";

  // pagination
  const overallStartIndex = (modalCurrentPage - 1) * rowsPerPage;
  const overallEndIndex = overallStartIndex + rowsPerPage;
  const overallPaginatedLeads = userLeads.slice(
    overallStartIndex,
    overallEndIndex
  );

  // ========== MONTHLY DATA ==========
  // const monthlyLeads =
  //   selectedMonth && selectedUser
  //     ? getUserLeadsByMonth(selectedUser.id, selectedMonth) || []
  //     : [];

  const monthlyClosed = monthlyLeads.filter((l) => l.lead_status === "Closed");
  const monthlyTotalLeads = monthlyLeads.length;
  const monthlyClosedCount = monthlyClosed.length;
  const monthlyRevenueClosed = getTotalRevenueClosed(monthlyLeads);
  const monthlyConversion =
    monthlyTotalLeads > 0
      ? ((monthlyClosedCount / monthlyTotalLeads) * 100).toFixed(1)
      : "0.0";

  const monthlyPaginatedLeads = monthlyLeads.slice(
    overallStartIndex,
    overallEndIndex
  );

  // ========== MEMOIZED CHART DATA ==========
  const overallPieData = useMemo(() => renderPieData(userLeads), [userLeads]);
  const overallBarData = useMemo(() => renderBarData(userLeads), [userLeads]);

  const monthlyPieData = useMemo(
    () => renderPieData(monthlyLeads),
    [monthlyLeads]
  );
  const monthlyBarData = useMemo(
    () => renderBarData(monthlyLeads),
    [monthlyLeads]
  );

  const areaSeries = Array.isArray(sLineArea.series)
    ? (sLineArea.series as { name?: string; data: (number | null)[] }[])
    : [];

  // Single no-data condition
  const isNoWonData =
    areaSeries.length === 0 ||
    areaSeries.every(
      (s) => !s.data || s.data.every((v) => v === 0)
    );

  // Normalize Apex line series (TypeScript safe)
  const lineSeries = Array.isArray(chartOptions1.series)
    ? (chartOptions1.series as { name?: string; data: (number | null)[] }[])
    : [];

  // Single no-data flag
  const isNoCompanyData =
    lineSeries.length === 0 ||
    lineSeries.every(
      (s) => !s.data || s.data.every((v) => v === 0)
    );

  // Normalize radial series (TS-safe)
  const radialSeries = Array.isArray(radialChart.series)
    ? (radialChart.series as number[])
    : [];

  // Single no-data flag
  const isNoLeadStatusData =
    radialSeries.length === 0 ||
    radialSeries.every((v) => v === 0);

  const isSeriesEmpty = (series?: any[]) =>
    !series?.[0]?.data?.length ||
    series[0].data.every((v: number) => v === 0);

  const isMonthlyWonEmpty = isSeriesEmpty(sLineArea.series);

  const hasDealsByYearData =
    derivedDealsByYearOptions?.series?.some(
      (s: any) => Array.isArray(s.data) && s.data.some((v: number) => v > 0)
    );


  return (
    <>
      <PageLoadProgressBar />
      <div className="scroll-progress-container">
        <div
          className="scroll-progress-bar"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row mb-0">
            <div className="col-md-12 mb-0">
              <div className="page-header">
                <div className="row align-items-center ">
                  <div className="col-12 col-md-6 col-lg-4 mb-3">
                    <h3 className="page-title fw-bold d-flex align-items-center">
                      <i className="ti ti-layout-dashboard text-primary me-2" />
                      <span className="text-truncate">Admin Dashboard</span>
                      <Tip
                        text="Overall summary of admin insights including users, leads, deals, activities and system performance."
                        placement="right"
                      >
                        <i
                          className="fa fa-info-circle ms-2 text-primary"
                          style={{ cursor: "pointer", fontSize: "15px" }}
                        />
                      </Tip>
                    </h3>
                  </div>
                  <div className="col-md-8 float-end ms-auto">
                    <div className="d-flex title-head">
                      <div className="daterange-picker d-flex align-items-center justify-content-center">
                        <div className="head-icons mb-0">
                          <CollapseHeader />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-0">
                  <i className="ti ti-user-check text-success me-2 "></i>
                  <span className="fw-semibold ">
                    Welcome, {userName || "Admin"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-0">
            <div className="row g-3 mb-4">
              {statusWidgets.map((widget, index) => {
                const total = statusWidgets.reduce(
                  (sum, w) => sum + w.value,
                  0
                );
                const percent =
                  total > 0 ? ((widget.value / total) * 100).toFixed(1) : 0;

                return (
                  <div className="col" key={index}>
                    <Tip
                      text={`${widget.subtext}: ${widget.value} (${percent}%)`}
                    >
                      <div
                        className={`card dashboard-card shadow-sm p-3 border-0 h-100 ${widget.color}`}
                        style={{
                          borderRadius: "16px",
                          cursor: "pointer",
                          transition:
                            "transform 0.25s ease, box-shadow 0.25s ease",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        onClick={() => {
                          if (widget.label === "UnAssigned Contacts") {
                            navigate(`/crm/contact-list?unassigned=true`);
                          } else {
                            const statusParam = widget.label || "";
                            navigate(
                              `/leads${statusParam
                                ? `?status=${encodeURIComponent(statusParam)}`
                                : ""
                              }`
                            );
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 18px rgba(0,0,0,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0px)";
                          e.currentTarget.style.boxShadow =
                            "var(--bs-box-shadow-sm)";
                        }}
                      >
                        <div className="mb-2 d-flex justify-content-center">
                          <span
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: "48px",
                              height: "48px",
                              background: "rgba(255,255,255,0.15)",
                              backdropFilter: "blur(4px)",
                            }}
                          >
                            {widget.icon}
                          </span>
                        </div>

                        <h3 className="fw-bold mb-1">
                          <CountUp
                            end={widget.value}
                            duration={2}
                            separator=","
                            decimals={widget.value % 1 !== 0 ? 1 : 0}
                          />
                        </h3>
                        <p className="text-muted mb-0">{widget.subtext}</p>
                      </div>
                    </Tip>
                  </div>
                );
              })}
            </div>
            <div className="col-xl-6 col-lg-12 d-flex">
              <div className="card flex-fill" id="monthly-highlights-card">
                <div
                  style={{ overflow: "visible" }}
                  className="card-header pb-2 d-flex align-items-center justify-content-between flex-nowrap"
                >
                  {/* LEFT SIDE : TITLE + ICONS */}
                  <h5
                    className="mb-0 d-flex align-items-center gap-2 flex-nowrap"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <i className="ti ti-help-circle" />
                    <span className="text-truncate">Monthly Highlights</span>

                    <Tip text="This donut shows the count of contacts, leads, and deals created this month.">
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h5>

                  {/* RIGHT SIDE: COLOR DOTS */}
                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <ChartExportControl
                      variant="dots"
                      data={leadDealStatsList?.map((item) => ({
                        label: item.label,
                        value: item.value,
                      })) ?? []}
                      filename="Monthly_Highlights"
                      cardId="monthly-highlights-card"
                    />
                  </div>
                </div>

                <div className="card-body">
                  {monthlyLeadDealStatsChart.series.every((v: number) => v === 0) ? (
                    <div className="text-center text-muted py-4">
                      <i className="ti ti-chart-pie-off fs-2 mb-2" />
                      <p className="mb-0">No data available</p>
                    </div>
                  ) : (
                    <Suspense fallback={<Loader />}>
                      <ReactApexChart
                        id="monthlyActivitiesChart"
                        options={monthlyLeadDealStatsChart.options}
                        series={monthlyLeadDealStatsChart.series}
                        type="donut"
                        height={240}
                      />
                    </Suspense>
                  )}

                  {/* LIST BELOW CHART */}
                  <div className="mt-3">
                    {leadDealStatsList.length === 0 ? (
                      <p className="text-center text-muted f-13 mb-0">
                        No monthly activity
                      </p>
                    ) : (
                      leadDealStatsList.map((item, index) => {
                        const total = leadDealStatsList.reduce(
                          (sum, item, i) =>
                            sum + (visibleSlices[i] ? item.value : 0),
                          0
                        );

                        const percent =
                          total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;

                        return (
                          <div
                            key={item.label}
                            className="d-flex align-items-center justify-content-between mb-2 flex-nowrap monthly-item"
                            style={{
                              cursor: "pointer",
                              opacity: visibleSlices[index] ? 1 : 0.5,
                              whiteSpace: "nowrap",
                            }}
                            onClick={() => toggleSlice(index)}
                          >
                            <p className="f-13 mb-0 d-flex align-items-center gap-2 flex-nowrap">
                              {/* Legend Color Tooltip */}
                              <Tip text={item.label}>
                                <i
                                  className="ti ti-circle-filled"
                                  style={{ color: item.color }}
                                />
                              </Tip>

                              {/* Label + Info Tooltip */}
                              <span
                                className="text-truncate d-flex align-items-center"
                                style={{
                                  maxWidth: "140px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item.label}

                                <Tip text={item.tooltip}>
                                  <i
                                    className="fa fa-info-circle text-muted ms-1"
                                    style={{
                                      fontSize: "12px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </Tip>
                              </span>
                            </p>

                            {/* RIGHT */}
                            <p className="f-13 fw-medium text-gray-9 flex-shrink-0 d-flex align-items-center gap-2 mb-0">
                              {item.value}
                              <span className="badge bg-light text-dark">
                                {percent}%
                              </span>
                            </p>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-6 col-lg-12 d-flex">
              <div className="card flex-fill" id="leads-by-stage-card">
                <div style={{ overflow: "visible" }} className="card-header border-0 pb-0">
                  <div
                    className="d-flex align-items-center justify-content-between flex-nowrap"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <h4 className="mb-0 d-flex align-items-center gap-2 text-truncate">
                      <i className="ti ti-info-square me-1" />
                      Leads by Stage
                      <Tip text="Displays how many leads are present in each stage of your pipeline.">
                        <i
                          className="fa fa-info-circle text-primary ms-2"
                          style={{ cursor: "pointer", fontSize: "15px" }}
                        />
                      </Tip>
                    </h4>

                    <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                      <ChartExportControl
                        variant="dots"
                        data={leadsByStageOptions?.series?.map((s: any) => ({
                          Stage: s.name,
                          Count: s.data?.[0] ?? 0,
                        })) ?? []}
                        filename="Leads_by_Stage"
                        cardId="leads-by-stage-card"
                      />
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* ===== SINGLE NO DATA CHECK ===== */}
                  {(
                    leadsByStageOptions.series.length === 0 ||
                    leadsByStageOptions.series.every(
                      (s: any) => s.data?.every((v: number) => v === 0)
                    )
                  ) && Object.keys(stageOwnerCounts).length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-chart-bar-off fs-2 mb-2" />
                      <p className="mb-0">No stage data available</p>
                    </div>
                  ) : (
                    <>
                      {/* ===== CHART ===== */}
                      <div id="deals-chart" ref={chartRef}>
                        <Suspense fallback={<Loader />}>
                          <ReactApexChart
                            options={leadsByStageOptions.options}
                            series={leadsByStageOptions.series}
                            type="bar"
                            height={275}
                          />
                        </Suspense>
                      </div>

                      {/* ===== STAGE → OWNER BREAKDOWN ===== */}
                      <div className="row mt-3">
                        {Object.entries(stageOwnerCounts).map(
                          ([stage, owners], idx) => {
                            const isOpen = openIndex === idx;

                            const totalStageCount = Object.values(owners).reduce(
                              (a: number, b: number) => a + b,
                              0
                            );

                            return (
                              <div className="col-md-6 col-lg-4 mb-3" key={stage}>
                                <div className="accordion-item shadow-sm rounded">
                                  <h2 className="accordion-header">
                                    <button
                                      className={`accordion-button py-2 text-wrap ${isOpen ? "" : "collapsed"
                                        }`}
                                      type="button"
                                      onClick={() => toggleAccordion(idx)}
                                    >
                                      <span className="fw-medium text-wrap">{stage}</span>

                                    </button>
                                  </h2>

                                  {/* BODY */}
                                  <div
                                    className={`accordion-collapse collapse ${isOpen ? "show" : ""
                                      }`}
                                  >
                                    <div
                                      className="accordion-body"
                                      style={{
                                        maxHeight: "220px",
                                        overflowY: "auto",
                                      }}
                                    >
                                      <ul className="list-group list-group-flush">
                                        {Object.entries(owners).map(
                                          ([owner, count]) => {
                                            const percent = totalStageCount
                                              ? (
                                                (count / totalStageCount) *
                                                100
                                              ).toFixed(1)
                                              : 0;

                                            return (
                                              <li
                                                key={owner}
                                                className="list-group-item d-flex justify-content-between align-items-start py-2"
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  navigate(
                                                    `/leads?owner=${encodeURIComponent(
                                                      owner
                                                    )}&stage=${encodeURIComponent(
                                                      stage
                                                    )}`
                                                  )
                                                }
                                              >
                                                {/* 👤 Owner with ellipsis + tooltip */}
                                                <span className="text-wrap">{owner}</span>


                                                <div
                                                  className="d-flex flex-column align-items-end text-end"
                                                >
                                                  <span className="badge bg-primary rounded-pill">
                                                    {count}
                                                  </span>
                                                  <small className="text-muted">
                                                    {percent}%
                                                  </small>
                                                </div>

                                              </li>
                                            );
                                          })}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6 d-flex">
              <div className="card flex-fill" id="product-deal-revenue-card">
                <div style={{ overflow: "visible" }} className="card-header border-0 pb-0">
                  <div
                    className="d-flex align-items-center justify-content-between flex-nowrap"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <h4 className="mb-0 d-flex align-items-center gap-2 text-truncate">
                      <i className="ti ti-packages me-1" />
                      Product-wise Deal Revenue
                      <Tip text="Showing current combined deal value of each product based on their status.">
                        <i
                          className="fa fa-info-circle text-primary ms-2"
                          style={{ cursor: "pointer", fontSize: "15px" }}
                        />
                      </Tip>
                    </h4>
                    <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                      <ChartExportControl
                        variant="dots"
                        data={productRevenueData ?? []}
                        filename="Product_Deal_Revenue"
                        cardId="product-deal-revenue-card"
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {/* ===== SINGLE NO DATA CHECK ===== */}
                  {productRevenueData.length === 0 ||
                    productRevenueData.every(
                      (p: any) =>
                        (p.won || 0) +
                        (p.lost || 0) +
                        (p.open || 0) +
                        (p.Closed || 0) ===
                        0
                    ) ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-database-off fs-2 mb-2" />
                      <p className="mb-0">No revenue data available</p>
                    </div>
                  ) : (
                    <div style={{ width: "100%", height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={productRevenueData}
                          margin={{ top: 20, right: 30, left: 0, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fontWeight: 600 }}
                            interval={0}
                            angle={-25}
                            textAnchor="end"
                          />

                          <Tooltip
                            formatter={(
                              value: number,
                              name: string,
                              props: any
                            ) => {
                              const won = Number(props.payload.won || 0);
                              const lost = Number(props.payload.lost || 0);
                              const open = Number(props.payload.open || 0);
                              const closed = Number(props.payload.Closed || 0);

                              const total = won + lost + open + closed;

                              const percent = total
                                ? ((Number(value) / total) * 100).toFixed(1)
                                : "0";

                              return [
                                `₹${Number(
                                  value
                                ).toLocaleString()} (${percent}%)`,
                                name,
                              ];
                            }}
                          />

                          <Legend
                            onClick={handleLegendClick}
                            verticalAlign="top"
                          />

                          {(!activeKey || activeKey === "Won") && (
                            <Bar
                              dataKey="won"
                              stackId="a"
                              fill="#28a745"
                              name="Won"
                            />
                          )}

                          {(!activeKey || activeKey === "Lost") && (
                            <Bar
                              dataKey="lost"
                              stackId="a"
                              fill="#dc3545"
                              name="Lost"
                            />
                          )}

                          {(!activeKey || activeKey === "Open") && (
                            <Bar
                              dataKey="open"
                              stackId="a"
                              fill="#ffc107"
                              name="Open"
                            />
                          )}

                          {(!activeKey || activeKey === "Closed") && (
                            <Bar
                              dataKey="Closed"
                              stackId="a"
                              fill="#17a2b8"
                              name="Closed"
                            />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Deals by Source */}
            <div className="col-lg-6 d-flex ">
              <div className="card flex-fill" id="deals-by-source-card">
                <div style={{ overflow: "visible" }} className="card-header border-0 pb-0 d-flex justify-content-between align-items-center">
                  <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-graph-up-arrow me-2"></i>Deals by Source
                    <Tip text="Shows the number of deals coming from each source.">
                      <i
                        className="fa fa-info-circle text-primary ms-1"
                        style={{ cursor: "pointer", fontSize: "15px" }}
                      />
                    </Tip>
                  </h4>
                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <ChartExportControl
                      variant="dots"
                      data={chartConfig?.labels?.map((label: string, i: number) => ({
                        Source: label,
                        Count: chartConfig.series?.[i] ?? 0,
                      })) ?? []}
                      filename="Deals_by_Source"
                      cardId="deals-by-source-card"
                    />
                  </div>
                </div>
                <div className="card-body">
                  {/* ===== SINGLE NO DATA CHECK ===== */}
                  {chartConfig.series.length === 0 ||
                    chartConfig.series.every((v: number) => v === 0) ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-chart-pie-off fs-2 mb-2" />
                      <p className="mb-0">No source data available</p>
                    </div>
                  ) : (
                    <Suspense fallback={<Loader />}>
                      <Chart
                        key={chartConfig.series.join("-")}
                        options={chartConfig}
                        series={chartConfig.series}
                        type="donut"
                        height={350}
                      />
                    </Suspense>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-12 d-flex">
              <div className="card flex-fill" id="team-performance-tracker-card">
                <div style={{ overflow: "visible" }} className="card-header pb-2 d-flex justify-content-between align-items-center flex-wrap">
                  <h5 className="mb-2 d-flex align-items-center gap-2">
                    <i className="ti ti-users text-primary me-1"></i>
                    Team Performance Tracker
                    <Tip text="This section shows each user's salary, targets and calculated incentives.">
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h5>
                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <ChartExportControl
                      variant="dots"
                      data={RevenueSeries ?? []}
                      filename="Team_Performance_Tracker"
                      cardId="team-performance-tracker-card"
                    />
                  </div>
                </div>

                <div className="card-body">
                  {targetData.length === 0 ||
                    targetData.every(
                      (t) =>
                        !t.salary &&
                        !t.target &&
                        !t.incentive_target &&
                        !t.total_deal_value &&
                        !t.incentive_percentage &&
                        !t.calculated_incentive
                    ) ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-users fs-2 me-2"></i>
                      <p className="mb-0">No target set yet.</p>
                    </div>
                  ) : (
                    <>
                      <Suspense fallback={<Loader />}>
                        {RevenueSeries.length > 0 &&
                          RevenueSeries[0].data.length > 0 && (
                            <ReactApexChart
                              options={RevenueChart}
                              series={RevenueSeries}
                              type="bar"
                              height={320}
                            />
                          )}
                      </Suspense>
                      {/* Toggle Button */}
                      <div className="mt-3">
                        <button
                          className="btn btn-link p-0 text-primary small"
                          onClick={() => setShowExplanation((prev) => !prev)}
                        >
                          {showExplanation
                            ? "Hide Explanation ▲"
                            : "Show Explanation ▼"}
                        </button>
                      </div>

                      {/* Collapsible Explanation */}
                      {showExplanation && (
                        <div className="mt-2 text-muted small border rounded p-3 bg-light">
                          <div className="d-flex align-items-start mb-2">
                            <i className="fa fa-info-circle text-primary me-2 mt-1" />
                            <span>
                              <strong>Salary:</strong> ₹10,000 is the base
                              monthly salary.
                            </span>
                          </div>
                          <div className="d-flex align-items-start mb-2">
                            <i className="fa fa-info-circle text-primary me-2 mt-1" />
                            <span>
                              <strong>Target:</strong> 20× salary → ₹2,00,000
                            </span>
                          </div>
                          <div className="d-flex align-items-start mb-2">
                            <i className="fa fa-info-circle text-primary me-2 mt-1" />
                            <span>
                              <strong>Incentive Target:</strong> 5× salary →
                              ₹50,000
                            </span>
                          </div>
                          <div className="d-flex align-items-start mb-2">
                            <i className="fa fa-info-circle text-primary me-2 mt-1" />
                            <span>
                              If <strong>target</strong> is achieved but{" "}
                              <strong>incentive target</strong> is not, then
                              incentive = 2× incentive target achieved from deal
                              value achieved
                            </span>
                          </div>
                          <div className="d-flex align-items-start">
                            <i className="fa fa-info-circle text-primary me-2 mt-1" />
                            <span>
                              If <strong>both</strong> are achieved, then
                              incentive = 5× incentive achieved
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="card shadow-sm mt-2">
            <div className="card-header text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0 d-flex align-items-center gap-2">
                🏅 Top 5 Performers Leaderboard
                <Tip text="Displays top 5 users by incentive earned or deal value">
                  <i
                    className="fa fa-info-circle text-primary"
                    style={{ cursor: "pointer", fontSize: "16px" }}
                  />
                </Tip>
              </h5>

              <div className="d-flex align-items-center gap-2">
                <label className="small mb-0">Filter:</label>
                <select
                  className="form-select form-select-sm bg-white text-dark"
                  style={{
                    width: "110px",        // ⬅ narrower
                    height: "28px",       // ⬅ shorter
                    fontSize: "12px",     // ⬅ smaller text
                    padding: "2px 6px",   // ⬅ tighter padding
                    lineHeight: "1.2",
                  }}
                  value={sortBy}
                  onChange={(e) =>
                    setSortBy(e.target.value as "all" | "2x" | "5x")
                  }
                >
                  <option value="all">All</option>
                  <option value="2x">2x Incentive</option>
                  <option value="5x">5x Incentive</option>
                </select>
              </div>
            </div>

            {topPerformers.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="ti ti-folder-off fs-2 mb-2" />
                <p className="mb-0">No performers data available</p>
              </div>
            ) : (
              <div className="table-responsive-sm">
                <table className="table table-striped table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Rank</th>
                      <th>User</th>
                      <th>Deal Value</th>
                      <th>Incentive Achieved</th>
                      <th>Incentive %</th>
                      <th>Incentive Level</th>
                      <th>Total Incentive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPerformers.map((user, index) => (
                      <tr
                        key={user.user_id}
                        className={index === 0 ? "top-rank" : ""}
                      >
                        <td>
                          {index === 0 ? (
                            <span className="text-warning me-2">🥇</span>
                          ) : index === 1 ? (
                            <span className="text-secondary me-2">🥈</span>
                          ) : index === 2 ? (
                            <span className="text-brown me-2">🥉</span>
                          ) : (
                            `#${index + 1}`
                          )}
                        </td>

                        <td className="d-flex align-items-center gap-2">
                          <img
                            src={`https://ui-avatars.com/api/?name=${user.user_name}&background=random`}
                            className="rounded-circle"
                            width="32"
                            height="32"
                          />
                          {user.user_name}
                        </td>

                        <td>₹{user.total_deal_value?.toLocaleString()}</td>

                        <td>
                          <div className="fw-bold">
                            ₹{user.incentive_achieved_amount?.toLocaleString()}
                          </div>
                          <div className="progress" style={{ height: "5px" }}>
                            <div
                              className="progress-bar bg-success"
                              style={{ width: `${user.incentive_percentage}%` }}
                            ></div>
                          </div>
                        </td>
                        <td>{user.incentive_percentage}%</td>
                        <td>
                          <span
                            className={`badge px-3 py-2 rounded-pill ${user.incentive_level?.includes("Fully")
                              ? "bg-success"
                              : "bg-warning text-dark"
                              }`}
                          >
                            {user.incentive_level}
                          </span>
                        </td>
                        <td className="fw-bold text-success">
                          ₹{user.calculated_incentive?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="card shadow-sm mt-2">
            <div className="card-header text-white d-flex justify-content-between align-items-center gap-3 flex-wrap">
              {/* Title */}
              <h5 className="mb-0 d-flex align-items-center gap-2">
                🕒 Lead Last Activity Overview
                <Tip text="This section displays the latest activity for each lead.">
                  <i
                    className="fa fa-info-circle text-primary"
                    style={{ cursor: "pointer", fontSize: "16px" }}
                  />
                </Tip>
              </h5>

              {/* Controls */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {/* Sort */}
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={toggleSort}
                >
                  Last Activity {sortDirection === "desc" ? "▼" : "▲"}
                </button>

                {/* Filter */}
                <select
                  className="form-select form-select-sm"
                  style={{
                    width: "140px",        // ⬅ narrower
                    height: "28px",       // ⬅ shorter
                    fontSize: "12px",     // ⬅ smaller text
                    padding: "2px 6px",   // ⬅ tighter padding
                    lineHeight: "1.2",
                  }}
                  value={activityFilter}
                  onChange={(e) => setActivityFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="fresh">Fresh (0–2 days)</option>
                  <option value="warm">Warm (3–7 days)</option>
                  <option value="cold">Cold (8–30 days)</option>
                  <option value="stale">Stale (&gt; 30 days)</option>
                  <option value="nofollow">No Follow-Up</option>
                </select>

                {/* Search */}
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search employee"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                />

              </div>
            </div>

            {/* Table */}
            <div className="card-body p-0">
              {filteredData.length > 0 ? (
                <div className="table-responsive-sm">
                  <table className="table table-striped table-hover mb-0 custom-sticky-header-table">
                    <thead className="table-light sticky-header">
                      <tr>
                        <th>Lead Owner</th>
                        <th>Company Name</th>
                        <th>Status</th>
                        <th>Last Activity</th>
                        <th>Created At</th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentPageData.map((lead) => {
                        const days = getDaysDiff(getActivityDate(lead));
                        const rowClass =
                          days <= 2
                            ? "fresh-row"
                            : days <= 7
                              ? "warm-row"
                              : days <= 30
                                ? "cold-row"
                                : "stale-row";

                        return (
                          <tr
                            key={lead.lead_id}
                            className={rowClass}
                            onClick={() =>
                              navigate(
                                `/leads?owner=${encodeURIComponent(
                                  lead.leadowner_name || ""
                                )}&company=${encodeURIComponent(
                                  lead.company_name || ""
                                )}`
                              )
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {/* Lead Owner + Avatar */}
                            <td className="d-flex align-items-center gap-2">
                              <img
                                src={`https://ui-avatars.com/api/?name=${lead.leadowner_name}&background=random`}
                                className="rounded-circle"
                                width="32"
                                height="32"
                              />
                              {highlightMatch(
                                lead.leadowner_name || "N/A",
                                employeeSearch
                              )}
                            </td>

                            {/* Company */}
                            <td>
                              {highlightMatch(lead.company_name || "N/A", employeeSearch)}
                            </td>

                            {/* Status */}
                            <td>
                              <span
                                className={`badge rounded-pill px-3 py-2 ${lead.status === "Contacted"
                                  ? "bg-success-subtle text-success"
                                  : "bg-danger-subtle text-danger"
                                  }`}
                              >
                                {lead.status.replace("_", " ")}
                              </span>
                            </td>

                            {/* Last Activity */}
                            <td>
                              <div className="fw-bold d-flex align-items-center gap-1">
                                {days <= 2 ? (
                                  <i className="ti ti-bolt text-success" />
                                ) : days <= 7 ? (
                                  <i className="ti ti-clock text-warning" />
                                ) : days <= 30 ? (
                                  <i className="ti ti-alert-circle text-danger" />
                                ) : (
                                  <i className="ti ti-alert-triangle text-danger" />
                                )}

                                {timeAgo(getActivityDate(lead))}
                              </div>

                              <div>{getActivityBadge(days)}</div>

                              <div
                                className="text-muted small"
                                title={getActivityReason(lead)}
                              >
                                {getActivityReason(lead)}
                              </div>

                              {days > 7 && (
                                <div className="text-danger small">
                                  ⚠️ Inactive {days} days
                                </div>
                              )}

                              {/* Progress bar */}
                              <div
                                className="progress mt-1"
                                style={{ height: "4px" }}
                              >
                                <div
                                  className="progress-bar"
                                  style={{
                                    width: `${Math.min(days * 3, 100)}%`,
                                    background:
                                      days > 30
                                        ? "#dc3545"
                                        : days > 7
                                          ? "#ffc107"
                                          : "#28a745",
                                  }}
                                ></div>
                              </div>
                            </td>

                            {/* Created At */}
                            <td>
                              {new Date(lead.created_at).toLocaleString(
                                undefined,
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="d-flex justify-content-end align-items-center p-3 gap-2 flex-wrap">
                    <button
                      className="btn btn-outline-secondary btn-sm pagination-btn"
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage(0)}
                    >
                      « First
                    </button>

                    <button
                      className="btn btn-outline-secondary btn-sm pagination-btn"
                      disabled={currentPage === 0}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 0))
                      }
                    >
                      ‹ Prev
                    </button>

                    <span className="pagination-info">
                      Page {currentPage + 1} of {pageCount}
                    </span>

                    <button
                      className="btn btn-outline-secondary btn-sm pagination-btn"
                      disabled={currentPage >= pageCount - 1}
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, pageCount - 1)
                        )
                      }
                    >
                      Next ›
                    </button>

                    <button
                      className="btn btn-outline-secondary btn-sm pagination-btn"
                      disabled={currentPage >= pageCount - 1}
                      onClick={() => setCurrentPage(pageCount - 1)}
                    >
                      Last »
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center text-muted py-5">
                  <i className="ti ti-alert-circle fs-2 mb-2" />
                  <p className="mb-0">No lead activity data found.</p>
                </div>
              )}
            </div>
          </div>

          <div className="row g-4">
            {/* Total Leads by Source */}
            <div className="col-lg-6">
              <div className="card h-100" id="leads-source-effectiveness-card">
                <div style={{ overflow: "visible" }} className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <i className="ti ti-chart-donut-3 me-1" />
                    Leads Source Effectiveness
                    <Tip
                      text="This chart shows how many leads came from each lead source."
                      placement="right"
                    >
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h4>

                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <ChartExportControl
                      variant="dots"
                      data={donutDetails?.map((item) => ({
                        Source: item.label,
                        Count: item.count,
                      })) ?? []}
                      filename="Leads_Source_Effectiveness"
                      cardId="leads-source-effectiveness-card"
                    />
                  </div>
                </div>

                <div className="card-body">
                  {Array.isArray(donutChart.series) &&
                    (donutChart.series as number[]).every((v) => v === 0) ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-chart-pie-off fs-2 mb-2" />
                      <p className="mb-0">No lead source data available</p>
                    </div>
                  ) : (
                    <>
                      <Suspense fallback={<Loader />}>
                        <ReactApexChart
                          id="leadSourceChart"
                          options={donutChart}
                          series={donutChart.series || []}
                          type="donut"
                          height={350}
                        />
                      </Suspense>

                      {/* Breakdown List */}
                      <div className="mt-4">
                        <h6 className="mb-3 fw-bold">Lead Source Breakdown:</h6>

                        <ul className="list-group list-group-flush">
                          {visibleSources.map((item, index) => (
                            <li
                              key={index}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <span className="d-flex align-items-center gap-2">
                                <span
                                  className="color-dot"
                                  style={{
                                    backgroundColor:
                                      donutChart.colors?.[index] || "#999",
                                  }}
                                ></span>
                                {item.label}
                              </span>

                              <span className="badge bg-primary rounded-pill">
                                {item.count}
                              </span>
                            </li>
                          ))}
                        </ul>

                        {/* Total */}
                        <li className="list-group-item d-flex justify-content-between align-items-center fw-bold py-1 px-2 border-top">
                          <strong>Total</strong>
                          <strong>
                            {donutDetails.reduce((sum, i) => sum + i.count, 0)}
                          </strong>
                        </li>

                        {/* Show More */}
                        {donutDetails.length > 3 && (
                          <div className="text-end mt-2">
                            <button
                              className="btn btn-sm btn-link p-0"
                              onClick={() => setShowAllSources((prev) => !prev)}
                            >
                              {showAllSources ? "Show Less" : "Show More"}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Closed Deals Monthly */}
            <div className="col-lg-6">
              <div className="card h-100" id="won-deals-monthly-card">
                <div style={{ overflow: "visible" }} className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <i className="ti ti-calendar-stats me-1" />
                    Won Deals Monthly
                    <Tip
                      text="This section displays the number of deals won in each month."
                      placement="right"
                    >
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h4>

                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary fs-10">
                      Total: ₹ {totalClosedValue.toLocaleString()}
                    </span>
                    <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                      <ChartExportControl
                        variant="dots"
                        data={monthlyClosedData?.map((item) => ({
                          Month: new Date(
                            item.year,
                            item.month - 1
                          ).toLocaleString("default", { month: "long" }),
                          Year: item.year,
                          "Closed Value": item.closed_value,
                        })) ?? []}
                        filename="Won_Deals_Monthly"
                        cardId="won-deals-monthly-card"
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {isMonthlyWonEmpty ? (
                    <div className="d-flex flex-column align-items-center justify-content-center"
                      style={{ height: 300 }}>
                      <i className="ti ti-chart-bar-off fs-1 text-muted mb-2"></i>
                      <span className="text-muted">
                        No won deals available for this period
                      </span>
                    </div>
                  ) : (

                    <ReactApexChart
                      options={sLineArea}
                      series={sLineArea.series}
                      type="area"
                      height={350}
                    />
                  )}

                  <div className="mt-4">
                    <ul className="list-group list-group-flush">
                      {visibleMonthlyClosed.map((item, index) => (
                        <li
                          key={index}
                          className="list-group-item d-flex justify-content-between align-items-center py-1 px-2"
                        >
                          {new Date(item.year, item.month - 1).toLocaleString(
                            "default",
                            {
                              month: "long",
                            }
                          )}
                          <span className="fw-bold text-primary">
                            ₹{" "}
                            {item.closed_value.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </li>
                      ))}

                      <li className="list-group-item d-flex justify-content-between align-items-center fw-bold py-1 px-2 border-top">
                        Total
                        <span className="text-success">
                          ₹{" "}
                          {monthlyClosedData
                            .reduce((sum, item) => sum + item.closed_value, 0)
                            .toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                        </span>
                      </li>
                    </ul>

                    {monthlyClosedData.length > 3 && (
                      <div className="text-end mt-2">
                        <button
                          className="btn btn-sm btn-link p-0"
                          onClick={() => setShowAllClosed((prev) => !prev)}
                        >
                          {showAllClosed ? "Show Less" : "Show More"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Performance */}
            <div
              className="bg-white rounded shadow-sm p-3"
              style={{ border: "1px solid #dee2e6" }}
            >
              <div
                className="mt-3 mb-3"
                style={{ paddingLeft: 0, paddingRight: 0 }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0 fw-bold text-black d-flex align-items-center gap-2">
                    <i className="ti ti-presentation-analytics me-1" />
                    Employee Performance
                    <Tip
                      text="This section evaluates employee productivity and performance metrics."
                      placement="right"
                    >
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h4>
                  <div className="input-group w-auto">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by user name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button className="btn btn-outline-primary" type="button">
                      Search
                    </button>
                  </div>
                </div>
                <div className="row">
                  {filteredUsers?.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-user-off fs-2 mb-2" />
                      <p className="mb-0">No users found with this lead owner name.</p>
                    </div>
                  ) : (
                    filteredUsers?.map((user) => {
                      const userLeads = getUserLeads(user.id);
                      const closedLeads = userLeads.filter(
                        (l) => l.lead_status === "Closed"
                      ).length;

                      return (
                        <div key={user.id} className="col-md-3 mb-4">
                          <div className="card shadow rounded-4 h-100 p-3 border-primary border-top border-3 position-relative">
                            <h6 className="fw-bold text-primary mb-2 text-truncate">
                              <i className="fas fa-user me-2 text-muted" />
                              {user.name}
                            </h6>
                            <div className="small mt-1">
                              <i className="bi bi-telephone-fill me-1 text-muted" />{" "}
                              Total Leads: {userLeads.length}
                            </div>
                            <div className="small mt-1">
                              <i className="fas fa-handshake me-1 text-info" />{" "}
                              Total Deals: {userLeads.length}
                            </div>
                            <div className="small mt-1">
                              <i className="fas fa-flag-checkered me-1 text-success" />{" "}
                              Closed: {closedLeads}
                            </div>
                            <div className="small mt-1">
                              <i className="fas fa-bars-progress me-1 text-primary" />{" "}
                              Progress:
                            </div>
                            <div
                              className="progress my-2 rounded-pill"
                              style={{ height: "10px" }}
                            >
                              {LEAD_STATUS_ORDER.map((status, i) => (
                                <div
                                  key={status}
                                  className="progress-bar"
                                  title={`${status}: ${groupBy(userLeads, (l) => l.lead_status)[
                                    status
                                  ] || 0
                                    }`}
                                  style={{
                                    width: `${((groupBy(
                                      userLeads,
                                      (l) => l.lead_status
                                    )[status] || 0) /
                                      userLeads.length) *
                                      100
                                      }%`,
                                    backgroundColor: COLORS[i % COLORS.length],
                                  }}
                                ></div>
                              ))}
                            </div>
                            <button
                              className="btn btn-sm btn-outline-primary position-absolute bottom-0 end-0 m-2"
                              onClick={() => {
                                setSelectedUser(user);
                                setModalCurrentPage(1);
                                // Default month = latest available
                                const months = getUserAvailableMonths(user.id);
                                if (months.length > 0) {
                                  setSelectedMonth(months[0]);
                                }
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* User Modal */}
                  <Modal
                    show={!!selectedUser}
                    onHide={closeInvoice}
                    size="lg"
                    centered
                  >
                    <Modal.Header className="border-0 pb-2">
                      <div className="d-flex align-items-center gap-2">
                        <div className="avatar avatar-sm rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center">
                          <i className="ti ti-user text-primary" />
                        </div>
                        <div>
                          <Modal.Title className="text-primary mb-0">
                            Lead Owner: {selectedUser?.name}
                          </Modal.Title>
                          <small className="text-muted">
                            Performance overview & monthly breakdown
                          </small>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn-close custom-btn-close border-0 ms-3"
                        onClick={closeInvoice}
                      />
                    </Modal.Header>

                    <Modal.Body className="pt-0">
                      {selectedUser &&
                        (() => {
                          return (
                            <>
                              {/* Tabs */}
                              <div className="d-flex mb-3">
                                <button
                                  className={`btn btn-sm rounded-pill ${activeTab === "overall"
                                    ? "btn-primary"
                                    : "btn-outline-primary"
                                    }`}
                                  onClick={() => setActiveTab("overall")}
                                >
                                  Overall
                                </button>
                                <button
                                  className={`btn btn-sm rounded-pill ms-2 ${activeTab === "monthly"
                                    ? "btn-primary"
                                    : "btn-outline-primary"
                                    }`}
                                  onClick={() => setActiveTab("monthly")}
                                >
                                  Monthly
                                </button>
                              </div>
                              <>
                                {/* ============ OVERALL TAB ============ */}
                                {activeTab === "overall" && (
                                  <div className="row">
                                    {/* Overall KPI summary */}
                                    <div className="col-md-4 mb-3">
                                      <div className="card border-0 shadow-sm p-3 h-100 rounded-4">
                                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                          <i className="ti ti-gauge text-primary" />
                                          Overall Summary
                                        </h6>

                                        <div className="fw-semibold">
                                          Total Leads:{" "}
                                          <span className="text-primary">
                                            {totalLeads1}
                                          </span>
                                        </div>

                                        <div className="fw-semibold mt-1">
                                          Closed Deals:{" "}
                                          <span className="text-success">
                                            {totalClosed}
                                          </span>
                                        </div>

                                        <div className="fw-semibold mt-1">
                                          Total Revenue Closed:{" "}
                                          <span className="text-success">
                                            ₹{totalRevenueClosed}
                                          </span>
                                        </div>

                                        <div className="fw-semibold mt-1">
                                          Conversion Rate:{" "}
                                          <span
                                            className={
                                              parseFloat(conversionRate1) >= 50
                                                ? "text-success"
                                                : parseFloat(conversionRate1) >=
                                                  25
                                                  ? "text-warning"
                                                  : "text-danger"
                                            }
                                          >
                                            {conversionRate1}%
                                          </span>
                                        </div>

                                        <div className="text-muted small mt-3">
                                          <i className="ti ti-briefcase me-1" />
                                          Industry: CRM
                                        </div>
                                        <div className="text-muted small">
                                          <i className="ti ti-map-pin me-1" />
                                          Region: South
                                        </div>
                                      </div>
                                    </div>

                                    {/* Pie chart - Lead Type Distribution */}
                                    <div className="col-md-4 mb-3">
                                      <div className="card shadow-sm border-0 rounded-4 h-100">
                                        <div className="card-header bg-white border-0 text-center py-2">
                                          <h6 className="fw-bold text-primary mb-0 d-flex justify-content-center align-items-center gap-2">
                                            <i className="ti ti-chart-donut" />
                                            Lead Type Distribution
                                          </h6>
                                        </div>

                                        <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                          <ResponsiveContainer
                                            width="100%"
                                            height={220}
                                          >
                                            <PieChart>
                                              <Pie
                                                data={overallPieData}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius={45}
                                                outerRadius={80}
                                                paddingAngle={3}
                                                animationDuration={900}
                                                labelLine={false}
                                                label={({ percent }) =>
                                                  `${(percent * 100).toFixed(
                                                    1
                                                  )}%`
                                                }
                                              >
                                                {COLORS.map((color, index) => (
                                                  <Cell
                                                    key={index}
                                                    fill={color}
                                                    stroke="#fff"
                                                    strokeWidth={2}
                                                  />
                                                ))}
                                              </Pie>

                                              {/* Center total label */}
                                              <text
                                                x="50%"
                                                y="50%"
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                style={{
                                                  fontSize: "16px",
                                                  fontWeight: 600,
                                                  fill: "#333",
                                                }}
                                              >
                                                {userLeads.length} Total
                                              </text>

                                              <Tooltip
                                                formatter={(
                                                  value: any,
                                                  name: any
                                                ) => [`${value} Leads`, name]}
                                                contentStyle={{
                                                  borderRadius: 10,
                                                  border: "none",
                                                  boxShadow:
                                                    "0 2px 6px rgba(0,0,0,0.15)",
                                                }}
                                              />
                                            </PieChart>
                                          </ResponsiveContainer>

                                          {/* Legend for Lead Type */}
                                          <div className="mt-3 w-100 px-3">
                                            {overallPieData.map((item, idx) => (
                                              <div
                                                key={idx}
                                                className="d-flex justify-content-between align-items-center mb-1"
                                                style={{ fontSize: "14px" }}
                                              >
                                                <span className="d-flex align-items-center gap-2">
                                                  <span
                                                    style={{
                                                      width: 12,
                                                      height: 12,
                                                      borderRadius: "50%",
                                                      background:
                                                        COLORS[
                                                        idx % COLORS.length
                                                        ],
                                                    }}
                                                  ></span>
                                                  {item.name}
                                                </span>
                                                <strong>{item.value}</strong>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Bar chart - Lead Status by Value */}
                                    <div className="col-md-4 mb-3">
                                      <div className="card shadow-sm border-0 rounded-4 h-100">
                                        <div className="card-header bg-white border-0 text-center py-2">
                                          <h6 className="fw-bold text-primary mb-0 d-flex justify-content-center align-items-center gap-2">
                                            <i className="ti ti-bar-chart-2" />
                                            Lead Status by Value
                                          </h6>
                                        </div>

                                        <div className="card-body p-2">
                                          <ResponsiveContainer
                                            width="100%"
                                            height={220}
                                          >
                                            <BarChart
                                              data={overallBarData}
                                              barCategoryGap="30%"
                                              margin={{
                                                top: 10,
                                                right: 20,
                                                left: 0,
                                                bottom: 5,
                                              }}
                                            >
                                              <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 10 }}
                                              />
                                              <YAxis
                                                allowDecimals={false}
                                                tick={{ fontSize: 10 }}
                                              />
                                              <Tooltip
                                                contentStyle={{
                                                  borderRadius: 10,
                                                  border: "none",
                                                  boxShadow:
                                                    "0 2px 8px rgba(0,0,0,0.2)",
                                                }}
                                              />
                                              <Bar
                                                dataKey="value"
                                                animationDuration={1000}
                                                radius={[6, 6, 0, 0]}
                                                label={{
                                                  position: "insideTop",
                                                  fill: "#fff",
                                                  fontSize: 10,
                                                }}
                                              >
                                                {overallBarData.map(
                                                  (entry, index) => (
                                                    <Cell
                                                      key={index}
                                                      fill={
                                                        STATUS_COLORS[
                                                        entry.name
                                                        ] ??
                                                        COLORS[
                                                        index % COLORS.length
                                                        ]
                                                      }
                                                    />
                                                  )
                                                )}
                                              </Bar>
                                            </BarChart>
                                          </ResponsiveContainer>

                                          {/* Legend under bar chart */}
                                          <div className="mt-2 px-3">
                                            {overallBarData.map((item, idx) => (
                                              <div
                                                key={idx}
                                                className="d-flex justify-content-between align-items-center mb-1"
                                                style={{ fontSize: "13px" }}
                                              >
                                                <span className="d-flex align-items-center gap-2">
                                                  <span
                                                    style={{
                                                      width: 12,
                                                      height: 12,
                                                      borderRadius: 4,
                                                      background:
                                                        STATUS_COLORS[
                                                        item.name
                                                        ] ??
                                                        COLORS[
                                                        idx % COLORS.length
                                                        ],
                                                    }}
                                                  ></span>
                                                  {item.name}
                                                </span>
                                                <strong>{item.value}</strong>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Lead Status Counts */}
                                    <div className="col-12">
                                      <h6 className="fw-bold mt-4 mb-3">
                                        Lead Status Counts
                                      </h6>
                                      <div className="row">
                                        {getLeadStatusCounts(userLeads).map(
                                          ({ status, count }) => (
                                            <div
                                              key={status}
                                              className="col-6 col-md-3 mb-3"
                                            >
                                              <div className="card text-center p-3 h-100 shadow-sm border-0">
                                                <h6 className="mb-1">
                                                  {status}
                                                </h6>
                                                <h4 className="text-primary mb-0">
                                                  {count}
                                                </h4>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>

                                      {/* All Leads Table */}
                                      <h6 className="fw-bold mt-4 mb-3">
                                        📋 All Leads of {selectedUser.name}
                                      </h6>
                                      <div className="table-responsive">
                                        <table className="table table-bordered table-striped table-sm align-middle">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Lead Name</th>
                                              <th>Status</th>
                                              <th>Lead Type</th>
                                              <th>Quoted Value</th>
                                              <th>Created At</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {overallPaginatedLeads.map(
                                              (lead) => (
                                                <tr key={lead.lead_id}>
                                                  <td>
                                                    {lead.contact
                                                      ?.contact_person_name ||
                                                      "-"}{" "}
                                                    |{" "}
                                                    {lead.contact
                                                      ?.company_name || "-"}
                                                  </td>
                                                  <td>{lead.lead_status}</td>
                                                  <td>
                                                    {lead.leadtype
                                                      ?.leadtype_name || "-"}
                                                  </td>
                                                  <td>
                                                    {lead.lead_value || "-"}
                                                  </td>
                                                  <td>
                                                    {new Date(
                                                      lead.created_at
                                                    ).toLocaleString("en-IN", {
                                                      day: "2-digit",
                                                      month: "2-digit",
                                                      year: "numeric",
                                                      hour: "2-digit",
                                                      minute: "2-digit",
                                                      second: "2-digit",
                                                      hour12: true,
                                                    })}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>

                                        {totalLeads1 === 0 && (
                                          <div className="text-center text-muted py-3">
                                            No leads found for this user.
                                          </div>
                                        )}

                                        {totalLeads1 > rowsPerPage && (
                                          <div className="d-flex justify-content-between align-items-center px-2 mt-2">
                                            <div className="text-muted small">
                                              Showing {overallStartIndex + 1}–
                                              {Math.min(
                                                overallEndIndex,
                                                totalLeads1
                                              )}{" "}
                                              of {totalLeads1} leads
                                            </div>
                                            <nav>
                                              <ul className="pagination pagination-sm mb-0">
                                                {Array.from({
                                                  length: Math.ceil(
                                                    totalLeads1 / rowsPerPage
                                                  ),
                                                }).map((_, index) => (
                                                  <li
                                                    key={index}
                                                    className={`page-item ${index + 1 ===
                                                      modalCurrentPage
                                                      ? "active"
                                                      : ""
                                                      }`}
                                                  >
                                                    <button
                                                      className="page-link"
                                                      onClick={() =>
                                                        setModalCurrentPage(
                                                          index + 1
                                                        )
                                                      }
                                                    >
                                                      {index + 1}
                                                    </button>
                                                  </li>
                                                ))}
                                              </ul>
                                            </nav>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* ============ MONTHLY TAB ============ */}
                                {activeTab === "monthly" && (
                                  <>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <h6 className="fw-bold mb-0">
                                        Monthly Performance
                                      </h6>
                                      <select
                                        className="form-select w-auto"
                                        value={selectedMonth}
                                        onChange={(e) => {
                                          setSelectedMonth(e.target.value);
                                          setModalCurrentPage(1);
                                        }}
                                      >
                                        {getUserAvailableMonths(
                                          selectedUser.id
                                        ).map((m) => (
                                          <option key={m} value={m}>
                                            {new Date(m + "-01").toLocaleString(
                                              "default",
                                              {
                                                month: "long",
                                                year: "numeric",
                                              }
                                            )}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="row">
                                      {/* Monthly KPIs */}
                                      <div className="col-md-4 mb-3">
                                        <div className="card border-0 shadow-sm p-3 h-100 rounded-4">
                                          <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                            <i className="ti ti-calendar-stats text-primary" />
                                            Monthly Summary
                                          </h6>
                                          <div className="fw-semibold">
                                            Total Leads Monthly:{" "}
                                            <span className="text-primary">
                                              {monthlyTotalLeads}
                                            </span>
                                          </div>
                                          <div className="fw-semibold mt-1">
                                            Closed this Month:{" "}
                                            <span className="text-success">
                                              {monthlyClosedCount}
                                            </span>
                                          </div>
                                          <div className="fw-semibold mt-1">
                                            Deals Closed Revenue:{" "}
                                            <span className="text-success">
                                              ₹{monthlyRevenueClosed}
                                            </span>
                                          </div>
                                          <div className="fw-semibold mt-1">
                                            Monthly Conversion Rate:{" "}
                                            <span
                                              className={
                                                parseFloat(monthlyConversion) >=
                                                  50
                                                  ? "text-success"
                                                  : parseFloat(
                                                    monthlyConversion
                                                  ) >= 25
                                                    ? "text-warning"
                                                    : "text-danger"
                                              }
                                            >
                                              {monthlyConversion}%
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Monthly Lead Type Pie */}
                                      <div className="col-md-4 mb-3">
                                        <div className="card shadow-sm border-0 rounded-4 h-100">
                                          <div className="card-header bg-white border-0 text-center py-2">
                                            <h6 className="fw-bold text-primary mb-0">
                                              Lead Type (Monthly)
                                            </h6>
                                          </div>
                                          <div className="card-body d-flex justify-content-center align-items-center">
                                            <ResponsiveContainer
                                              width="100%"
                                              height={180}
                                            >
                                              <PieChart>
                                                <Pie
                                                  data={monthlyPieData}
                                                  dataKey="value"
                                                  nameKey="name"
                                                  outerRadius={60}
                                                  paddingAngle={3}
                                                  label={({ name, value }) =>
                                                    `${name}: ${value}`
                                                  }
                                                >
                                                  {COLORS.map(
                                                    (color, index) => (
                                                      <Cell
                                                        key={index}
                                                        fill={color}
                                                      />
                                                    )
                                                  )}
                                                </Pie>
                                                <Tooltip />
                                              </PieChart>
                                            </ResponsiveContainer>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Monthly Lead Status by Value */}
                                      <div className="col-md-4 mb-3">
                                        <div className="card shadow-sm border-0 rounded-4 h-100">
                                          <div className="card-header bg-white border-0 text-center py-2">
                                            <h6 className="fw-bold text-primary mb-0">
                                              Lead Status by Value (Monthly)
                                            </h6>
                                          </div>
                                          <div className="card-body d-flex justify-content-center align-items-center">
                                            <ResponsiveContainer
                                              width="100%"
                                              height={180}
                                            >
                                              <BarChart data={monthlyBarData}>
                                                <XAxis
                                                  dataKey="name"
                                                  tick={{ fontSize: 10 }}
                                                />
                                                <YAxis
                                                  allowDecimals={false}
                                                  tick={{ fontSize: 10 }}
                                                />
                                                <Tooltip />
                                                <Bar
                                                  dataKey="value"
                                                  fill="#1890ff"
                                                  radius={[4, 4, 0, 0]}
                                                />
                                              </BarChart>
                                            </ResponsiveContainer>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Monthly Status Blocks & Table */}
                                    <div className="col-12">
                                      <h6 className="fw-bold mt-4 mb-3">
                                        Lead Status Counts
                                      </h6>
                                      <div className="row">
                                        {getLeadStatusCounts(monthlyLeads).map(
                                          ({ status, count }) => (
                                            <div
                                              key={status}
                                              className="col-6 col-md-3 mb-3"
                                            >
                                              <div className="card text-center p-3 h-100 shadow-sm border-0">
                                                <h6 className="mb-1">
                                                  {status}
                                                </h6>
                                                <h4 className="text-primary mb-0">
                                                  {count}
                                                </h4>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>

                                      <div className="table-responsive">
                                        <table className="table table-bordered table-striped table-sm align-middle">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Lead Name</th>
                                              <th>Status</th>
                                              <th>Lead Type</th>
                                              <th>Value</th>
                                              <th>Created At</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {monthlyPaginatedLeads.map(
                                              (lead) => (
                                                <tr key={lead.lead_id}>
                                                  <td>
                                                    {lead.contact
                                                      ?.contact_person_name ||
                                                      "-"}{" "}
                                                    |{" "}
                                                    {lead.contact
                                                      ?.company_name || "-"}
                                                  </td>
                                                  <td>{lead.lead_status}</td>
                                                  <td>
                                                    {lead.leadtype
                                                      ?.leadtype_name || "-"}
                                                  </td>
                                                  <td>
                                                    {lead.lead_value || "-"}
                                                  </td>
                                                  <td>
                                                    {new Date(
                                                      lead.created_at
                                                    ).toLocaleDateString(
                                                      "en-IN"
                                                    )}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </>
                            </>
                          );
                        })()}
                    </Modal.Body>
                  </Modal>
                </div>

                {/* Overall Performance Table */}
                <div className="table-responsive mb-4">
                  <table className="table table-bordered table-striped table-hover align-middle">
                    <thead className="table-light ">
                      <tr>
                        <th>Lead Person</th>
                        <th>Leads Handled</th>
                        <th>Deals Created</th>
                        <th>Deals Won</th>
                        <th>Deal Conversion Rate (%)</th>
                        <th>Revenue Closed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleData.map((data) => {
                        const winRateNum = parseFloat(
                          data.winRate.replace("%", "")
                        );
                        let rateColor = "text-secondary";
                        if (winRateNum >= 70) rateColor = "text-success";
                        else if (winRateNum >= 40) rateColor = "text-warning";
                        else rateColor = "text-danger";

                        return (
                          <tr key={data.salesperson}>
                            <td>
                              <i className="fa fa-user-circle me-1 text-primary" />{" "}
                              {data.salesperson}
                            </td>
                            <td>{data.leadsHandled}</td>
                            <td>
                              <span className="badge bg-info text-dark">
                                {data.dealsCreated}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-success">
                                {data.dealsWon}
                              </span>
                            </td>
                            <td className={rateColor}>
                              <i className="fa fa-bullseye me-1" />
                              {data.winRate}
                            </td>
                            <td>
                              <i className="fa fa-inr me-1" />
                              <strong>{data.revenueClosed}</strong>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* <div className="col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header border-0 pb-0">
                  <h4>
                    <i className="ti ti-table me-1" />
                    Lead Stage Summary
                  </h4>
                </div>
                <div className="card-body p-0">
                  <table className="table table-bordered align-middle text-center table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Lead Stage Name</th>
                        <th>Lead Owners</th>
                        <th>Companys</th>
                        <th>Total Leads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadsByStageDetailed.map((group, idx) => (
                        <tr key={`${group.lead_stage_id}_${idx}`}>
                          <td className="fw-semibold text-capitalize">
                            {group.lead_stage_name}
                          </td>

                          <td className="text-start">
                            <ul className="list-unstyled mb-0 small">
                              {Array.from(
                                new Set(
                                  group.details.map((d) => d.company_name)
                                )
                              ).map((name, i) => (
                                <li key={i} className="mb-1">
                                  <i className="ti ti-building" /> {name}
                                </li>
                              ))}
                            </ul>
                          </td>

                          <td className="text-start">
                            <ul className="list-unstyled mb-0 small">
                              {Array.from(
                                new Set(
                                  group.details.map((d) => d.lead_owner_name)
                                )
                              ).map((owner, i) => (
                                <li key={i}>
                                  <i className="ti ti-user" /> {owner}
                                </li>
                              ))}
                            </ul>
                          </td>

                          <td className="fw-bold text-center">
                            {group.total_leads}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div> */}

            {/* Company by Year Chart */}
            <div className="col-md-12 d-flex">
              <div className="card shadow flex-fill" id="company-by-year-card">
                <div style={{ overflow: "visible" }} className="card-header d-flex justify-content-between align-items-center flex-wrap row-gap-2">
                  <h4 className="d-flex align-items-center gap-2">
                    <i className="bi bi-bar-chart-line me-2"></i>
                    Company by Year
                    <Tip
                      text="This chart shows the number of companies added each year."
                      placement="right"
                    >
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h4>

                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <ChartExportControl
                      variant="dots"
                      data={lineSeries ?? []}
                      filename="Company_by_Year"
                      cardId="company-by-year-card"
                    />
                  </div>
                </div>
                <div
                  className="card-body"
                  style={{ height: "300px", width: "100%" }}
                >
                  {isNoCompanyData ? (
                    /* ===== NO DATA ===== */
                    <div className="text-center text-muted">
                      <i className="ti ti-chart-bar-off fs-2 mb-2" />
                      <p className="mb-0">No company data available</p>
                    </div>
                  ) : (

                    <Chart
                      series={lineSeries}
                      options={chartOptions1}
                      type="line"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100" id="deal-pipeline-by-stage-card">
                <div style={{ overflow: "visible" }} className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-bar-chart-fill me-2" />
                    Deal Pipeline by Stage
                    <Tip text="Visual representation of deals distributed across different pipeline stages.">
                      <i
                        className="fa fa-info-circle text-primary ms-1"
                        style={{ cursor: "pointer", fontSize: "15px" }}
                      />
                    </Tip>
                  </h4>
                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <ChartExportControl
                      variant="dots"
                      data={stages?.map((s) => ({
                        Stage: s.lead_stage_name,
                        Deals: s.total_leads,
                      })) ?? []}
                      filename="Deal_Pipeline_by_Stage"
                      cardId="deal-pipeline-by-stage-card"
                    />
                  </div>
                </div>

                <div className="card-body">
                  {stages.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-filter-off fs-2 mb-2" />
                      <p className="mb-0">No stage data available</p>
                    </div>
                  ) : (
                    stages.map((stage, idx) => {
                      const percent = max
                        ? ((stage.total_leads / max) * 100).toFixed(1)
                        : 0;
                      return (
                        <div
                          key={idx}
                          className="mb-3 pb-1 border-bottom"
                          onClick={() =>
                            navigate(
                              `/leads?stage=${encodeURIComponent(
                                stage.lead_stage_name
                              )}`
                            )
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {/* Row Header */}
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong className="text-truncate">
                              {stage.lead_stage_name}
                            </strong>

                            <span className="small text-muted">
                              {stage.total_leads} leads • {percent}%
                            </span>
                          </div>

                          {/* Bar Container */}
                          <div
                            className="position-relative"
                            style={{
                              height: "26px",
                              borderRadius: "8px",
                              background: "#E5E7EB",
                            }}
                          >
                            {/* Filled Portion */}
                            <div
                              style={{
                                width: `${percent}%`,
                                height: "100%",
                                borderRadius: "8px",
                                background:
                                  "linear-gradient(90deg, #3B82F6, #60A5FA)",
                                display: "flex",
                                alignItems: "center",
                                paddingLeft: "10px",
                                color: "white",
                                fontWeight: 600,
                                transition: "width 0.3s ease",
                              }}
                            >
                              {stage.total_leads}
                            </div>

                            {/* Percentage Badge */}
                            <span
                              className="badge bg-primary position-absolute"
                              style={{
                                right: "-6px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "10px",
                                padding: "3px 6px",
                              }}
                            >
                              {percent}%
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Lead Funnel */}
            <div className="col-lg-6">
              <div className="card h-100" id="leads-funnel-overview-card-deals">
                <div style={{ overflow: "visible" }} className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <i className="ti ti-stairs-up  me-1"></i> Leads Funnel
                    Overview
                    <Tip
                      text="This funnel chart shows the progression of leads through different stages, based on total lead count."
                      placement="right"
                    >
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h4>
                  <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                    <ChartExportControl
                      variant="dots"
                      data={funnelData?.sortedData?.map((item: any) => ({
                        Stage: item.name,
                        Count: item.value,
                      })) ?? []}
                      filename="Leads_Funnel_Overview"
                      cardId="leads-funnel-overview-card-deals"
                    />
                  </div>
                </div>

                <div className="card-body">
                  {funnelData.sortedData &&
                    funnelData.sortedData.every((item) => item.value === 0) ? (
                    <div className="text-center text-muted py-5">
                      <i className="ti ti-filter-off fs-2 mb-2" />
                      <p className="mb-0">No funnel data available</p>
                    </div>
                  ) : (
                    <>
                      <div id="lead-funnel-chart">
                        <Suspense fallback={<Loader />}>
                          <ReactApexChart
                            id="leadFunnelChart"
                            options={{
                              chart: {
                                height: 420,
                                type: "bar",
                                toolbar: { show: false },
                                events: {
                                  dataPointSelection: (
                                    event: any,
                                    chartContext: any,
                                    config: any
                                  ) => {
                                    const categories =
                                      config.w.config.xaxis?.categories || [];
                                    let label =
                                      categories[config.dataPointIndex];
                                    if (!label) return;

                                    if (label.includes("Total Leads")) {
                                      navigate("/leads");
                                      return;
                                    }

                                    let status = "";
                                    if (label.includes("Not Contacted"))
                                      status = "Not_Contacted";
                                    else if (label.includes("Contacted"))
                                      status = "Contacted";
                                    else if (label.includes("Closed Leads"))
                                      status = "Closed";
                                    else if (label.includes("Deals Won"))
                                      status = "Won";

                                    if (status) {
                                      navigate(
                                        `/leads?status=${encodeURIComponent(
                                          status
                                        )}`
                                      );
                                    }
                                  },
                                },
                              },
                              plotOptions: {
                                bar: {
                                  borderRadius: 0,
                                  horizontal: true,
                                  distributed: true,
                                  barHeight: "100%",
                                  isFunnel: true,
                                },
                              },
                              colors: funnelData.sortedData?.map((item) =>
                                item.value === 0
                                  ? "#D3D3D3"
                                  : [
                                    "#4A00E5",
                                    "#1ECBE2",
                                    "#FF9D0A",
                                    "#5CB85C",
                                  ][funnelData.sortedData.indexOf(item) % 4]
                              ) || ["#4A00E5", "#1ECBE2", "#FF9D0A", "#5CB85C"],
                              dataLabels: {
                                enabled: true,
                                dropShadow: { enabled: true },
                                formatter: (val: number, opts: any) =>
                                  funnelData.sortedData?.[opts.dataPointIndex]
                                    ?.name || "",
                                style: {
                                  fontWeight: "bold",
                                },
                              },
                              xaxis: {
                                categories:
                                  funnelData.sortedData?.map(
                                    (item) => item.name
                                  ) || [],
                              },
                              legend: { show: false },
                              tooltip: {
                                y: {
                                  formatter: (val: number) => `${val} leads`,
                                },
                              },
                            }}
                            series={[
                              {
                                name: "",
                                data:
                                  funnelData.sortedData?.map(
                                    (item) => item.value
                                  ) || [],
                              },
                            ]}
                            type="bar"
                            height={420}
                          />
                        </Suspense>
                      </div>

                      {/* ✅ Percentages based on total_leads */}
                      <div className="mt-3">
                        {funnelData.sortedData?.map((item, index) => {
                          const totalLeads = funnelData.total_leads || 1;
                          const percentage = (
                            (item.value / totalLeads) *
                            100
                          ).toFixed(1);
                          return (
                            <div
                              key={index}
                              className="d-flex justify-content-between px-3"
                              style={{ fontSize: "14px", marginBottom: "4px" }}
                            >
                              <span>{item.name}</span>
                              <span className="text-muted">{percentage}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="row g-4 align-items-stretch">
              {/* Lead Type Breakdown */}
              <div className="col-12 col-md-6 col-lg-3 d-flex">
                <div className="card h-100 w-100" id="lead-type-breakdown-card">
                  <div style={{ overflow: "visible" }} className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 d-flex align-items-center gap-2">
                      <i className="ti ti-chart-donut me-1" />
                      Lead Type Breakdown
                      <Tip text="This chart shows the distribution of leads based on their types.">
                        <i
                          className="fa fa-info-circle text-primary"
                          style={{ cursor: "pointer", fontSize: "16px" }}
                        />
                      </Tip>
                    </h5>
                    <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                      <ChartExportControl
                        variant="dots"
                        data={leadTypeSeries?.map((val, i) => ({
                          Type: leadTypeOptions?.labels?.[i] || "Unknown",
                          Count: val,
                        })) ?? []}
                        filename="Lead_Type_Breakdown"
                        cardId="lead-type-breakdown-card"
                      />
                    </div>
                  </div>
                  <div className="card-body">
                    {leadTypeCounts.length > 0 ? (
                      <Suspense fallback={<Loader />}>
                        <ReactApexChart
                          id="leadTypeChart"
                          options={leadTypeOptions}
                          series={leadTypeSeries}
                          type="donut"
                          height={300}
                        />
                      </Suspense>
                    ) : (
                      <div className="text-center text-muted py-5">
                        <i className="ti ti-chart-pie-off fs-2 mb-2" />
                        <p className="mb-0">No lead type data available.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Deal Status Summary (Rose Chart) */}
              <div className="col-12 col-md-6 col-lg-4 d-flex">
                <div className="card h-100 w-100 shadow-sm border-0" id="deal-status-summary-card">
                  <div style={{ overflow: "visible" }} className="card-header bg-white border-0 d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
                    <h5 className="mb-0 d-flex align-items-center gap-2 flex-wrap">
                      <i className="ti ti-chart-pie text-primary flex-shrink-0" />
                      <span className="text-truncate">Deal Status Summary</span>
                      <Tip text="Overall count of deals grouped by status.">
                        <i className="fa fa-info-circle text-primary" />
                      </Tip>
                    </h5>

                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-primary fs-10">
                        Total: {totalDeals.toLocaleString()} deals
                      </span>
                      <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                        <ChartExportControl
                          variant="dots"
                          data={dealStatusSummary?.map((item) => ({
                            Status: item.name,
                            Count: item.value,
                          })) ?? []}
                          filename="Deal_Status_Summary"
                          cardId="deal-status-summary-card"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    <Suspense fallback={<Loader />}>
                      <div className="echart-wrapper">
                        <ReactECharts
                          ref={chartRef}
                          option={roseOptions}
                          style={{ width: "100%" }}
                          onEvents={{ click: handleChartClick }}
                        />
                      </div>
                    </Suspense>
                  </div>
                </div>
              </div>

              {/* Lead Status Overview */}
              <div className="col-12 col-lg-5 d-flex">
                <div className="card h-100 w-100" id="lead-status-overview-card">
                  <div style={{ overflow: "visible" }} className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                      <i className="ti ti-adjustments-alt me-1" />
                      Lead Status Overview
                      <Tip text="Distribution of leads by their current status.">
                        <i
                          className="fa fa-info-circle text-primary"
                          style={{ cursor: "pointer", fontSize: "16px" }}
                        />
                      </Tip>
                    </h5>
                    <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                      <ChartExportControl
                        variant="dots"
                        data={radialChart?.labels?.map((label: string, i: number) => ({
                          Status: label,
                          Percentage: radialChart.series?.[i] ?? 0,
                        })) ?? []}
                        filename="Lead_Status_Overview"
                        cardId="lead-status-overview-card"
                      />
                    </div>
                  </div>

                  <div className="card-body d-flex align-items-center justify-content-center">
                    <Suspense fallback={<Loader />}>
                      {isNoLeadStatusData ? (
                        /* ===== NO DATA ===== */
                        <div className="text-center text-muted py-5">
                          <i className="ti ti-chart-pie-off fs-2 mb-2" />
                          <p className="mb-0">No lead status data available</p>
                        </div>
                      ) : (
                        <Chart
                          options={{
                            chart: {
                              type: "radialBar",
                              height: 320,
                              events: {
                                dataPointSelection: (_e, _ctx, config) => {
                                  const label =
                                    radialChart.labels[config.dataPointIndex];
                                  if (label)
                                    navigate(
                                      `/leads?status=${encodeURIComponent(
                                        label
                                      )}`
                                    );
                                },
                              },
                            },

                            plotOptions: radialChart.plotOptions,
                            labels: radialChart.labels,
                            colors: radialChart.colors.slice(
                              0,
                              radialChart.series.length
                            ),

                            legend: {
                              show: true,
                              position: "bottom",
                              fontSize: "13px",
                              formatter: (seriesName: string, opts: any) => {
                                const pct =
                                  opts.w.globals.series[opts.seriesIndex];
                                const count =
                                  radialChart.counts?.[opts.seriesIndex] ?? 0;
                                return `${seriesName}: ${count} (${pct}%)`;
                              },
                            },

                            tooltip: {
                              enabled: true,
                              y: {
                                formatter: (_val: number, opts: any) => {
                                  const count =
                                    radialChart.counts?.[opts.seriesIndex] ?? 0;
                                  const pct =
                                    radialChart.series[opts.seriesIndex];
                                  return `${count} Leads (${pct}%)`;
                                },
                              },
                            },
                          }}
                          series={radialSeries}
                          type="radialBar"
                          height={320}
                        />
                      )}
                    </Suspense>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Notes Section */}
          <div className="col-md-12 mt-3">
            <div className="card">
              <div className="card-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="d-flex align-items-center gap-2">
                  <i className="ti ti-notes me-1" />
                  Sticky Notes
                  <Tip
                    text="Use this section to add personal notes or important reminders."
                    placement="right"
                  >
                    <i
                      className="fa fa-info-circle text-primary"
                      style={{ cursor: "pointer", fontSize: "16px" }}
                    />
                  </Tip>
                </h4>

                <Button onClick={() => openModal()} className="mb-3">
                  + Add Note
                </Button>
              </div>
              <div className="card-body">
                {/* ===== SINGLE NO DATA CHECK ===== */}
                {notes.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="ti ti-note-off fs-2 mb-2" />
                    <p className="mb-0">No notes available</p>
                  </div>
                ) : (

                  <div className="sticky-note-container">
                    {notes.map((note, index) => (
                      <div
                        key={note.note_id}
                        className="sticky-note"
                        style={{ backgroundColor: getRandomNoteColor(index) }}
                      >
                        <button
                          className="delete-btn"
                          onClick={() => setSelectedNoteId(note.note_id)}
                          title="Delete"
                        >
                          ×
                        </button>
                        <div
                          className="note-content"
                          onClick={() => openModal(note)}
                          title="Click to edit"
                        >
                          <h4>
                            <i>Sticky Note</i> {/* Common static title */}
                          </h4>
                          <p>
                            {note.notes_decription || "No description provided"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Add/Edit Note Modal */}
            <Modal show={isModalOpen} onHide={closeModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  {selectedNote ? "Edit Note" : "Add Note"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveNote(selectedNote, noteDescription);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">
                      Note Description:<span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      value={noteDescription}
                      onChange={(e) => setNoteDescription(e.target.value)}
                      placeholder="Enter your note..."
                      required
                      rows={4}
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary">
                      {selectedNote ? "Update" : "Save"}
                    </Button>
                  </div>
                </form>
              </Modal.Body>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
              show={!!selectedNoteId}
              onHide={() => setSelectedNoteId(null)}
              centered
            >
              <Modal.Body>
                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Remove Note?</h4>
                  <p className="mb-0">
                    Are you sure you want to remove this note?
                  </p>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Button
                      variant="light"
                      onClick={() => setSelectedNoteId(null)}
                      className="me-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => confirmDeleteNote(selectedNoteId)}
                    >
                      Yes, Delete it
                    </Button>
                  </div>
                </div>
              </Modal.Body>
            </Modal>
          </div>

          {/*Sticky Notes*/}
          <div className="row">
            <div className="col-md-6 d-flex">
              <div className="card upcoming-card flex-fill shadow-sm rounded-4 border-0">
                {/* Header */}
                <div className="card-header d-flex justify-content-between align-items-center border-0 bg-white py-3">
                  <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <i className="ti ti-calendar-event text-primary "></i>
                    Upcoming Schedule
                    <Tip text="This section displays your upcoming meetings, tasks, and deadlines.">
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h5>

                  {/* Status Colors */}
                  <div className="d-flex gap-1">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                </div>

                {/* Body */}
                <div className="card-body p-0">
                  <div
                    className="event-scroll-wrapper calendar-hover"
                    style={{
                      maxHeight: "340px",
                      overflowY: "auto",
                      cursor: "pointer",
                    }}
                  >
                    {events.length > 0 ? (
                      events.map((event, index) => {
                        const start = moment(event.event_start);
                        const end = moment(event.event_end);

                        const bgColors = [
                          "#E9EAFE",
                          "#FFF1DA",
                          "#D6F5E8",
                          "#FFE9E9",
                        ];
                        const textColors = [
                          "#706FE5",
                          "#F4A700",
                          "#38B77C",
                          "#F25C5C",
                        ];

                        const color = textColors[index % textColors.length];
                        const bg = bgColors[index % bgColors.length];

                        return (
                          <div
                            key={event.id}
                            className="event-item d-flex justify-content-between align-items-center p-3 mb-2 rounded-4"
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            style={{
                              backgroundColor: bg,
                              cursor: "pointer",
                              transform:
                                hoveredIndex === index
                                  ? "scale(1.015)"
                                  : "scale(1)",
                              boxShadow:
                                hoveredIndex === index
                                  ? "0 4px 12px rgba(0,0,0,0.12)"
                                  : "0 1px 4px rgba(0,0,0,0.08)",
                              transition: "all 0.25s ease",
                              borderLeft: `5px solid ${color}`,
                            }}
                          >
                            {/* Date Block */}
                            <div
                              className="text-center me-3"
                              style={{
                                color,
                                minWidth: "70px",
                                lineHeight: "1.2",
                              }}
                            >
                              <div
                                className="fw-bold"
                                style={{
                                  fontSize: "1.8rem",
                                  letterSpacing: "-1px",
                                }}
                              >
                                {start.format("DD")}
                              </div>
                              <div
                                className="fw-semibold text-uppercase"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {start.format("MMM")}
                              </div>
                              <div
                                className="fw-medium"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {start.format("YYYY")}
                              </div>
                            </div>

                            {/* Event Content */}
                            <div className="flex-grow-1 pe-2">
                              <div className="fw-bold text-dark text-truncate d-flex align-items-center gap-1">
                                <i className="ti ti-bell text-muted"></i>
                                {event.title}
                              </div>

                              <div className="small fw-semibold text-muted d-flex align-items-center gap-1">
                                <i className="ti ti-clock"></i>
                                {start.format("hh:mm A")} –{" "}
                                {end.format("hh:mm A")}
                              </div>

                              {/* Optional Type Tag */}
                              <div
                                className="badge mt-1"
                                style={{
                                  backgroundColor: color,
                                  color: "#fff",
                                  fontSize: "0.65rem",
                                }}
                              >
                                {event.description || "Meeting"}
                              </div>
                            </div>

                            {/* Location */}
                            <div
                              className="text-end small fw-semibold"
                              style={{
                                whiteSpace: "nowrap",
                                minWidth: "90px",
                                color: color,
                              }}
                            >
                              <i className="ti ti-map-pin me-1"></i>
                              {event.location || "No location"}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center text-muted py-5">
                        <i className="ti ti-note-off fs-2 mb-2" />
                        <p className="mb-0">No upcoming events</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="card-footer border-0 d-flex justify-content-end bg-white py-3">
                  <Link
                    to={route.calendar}
                    className="btn btn-outline-primary btn-sm rounded-pill"
                  >
                    View Full Schedule
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-md-6 d-flex">
              <div className="card upcoming-card flex-fill shadow-sm rounded-4 border-0">
                {/* Header */}
                <div className="card-header d-flex justify-content-between align-items-center border-0 bg-white py-3">
                  <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <i className="ti ti-checklist text-primary "></i>
                    Recent ToDos
                    <Tip text="This section lists your most recently added or updated tasks.">
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: "16px" }}
                      />
                    </Tip>
                  </h5>

                  {/* Signal dots */}
                  <div className="d-flex gap-1">
                    <span className="dot red"></span>
                    <span className="dot yellow"></span>
                    <span className="dot green"></span>
                  </div>
                </div>

                {/* Body */}
                <div className="card-body p-0">
                  <div
                    className="event-scroll-wrapper"
                    style={{ maxHeight: "350px", overflowY: "auto" }}
                  >
                    {todos.length > 0 ? (
                      todos.map((todo, index) => {
                        const created = moment(todo.created_at);

                        const bgColors = [
                          "#E9EAFE",
                          "#FFF1DA",
                          "#D6F5E8",
                          "#FFE9E9",
                        ];
                        const textColors = [
                          "#706FE5",
                          "#F4A700",
                          "#38B77C",
                          "#F25C5C",
                        ];

                        const bg = bgColors[index % bgColors.length];
                        const color = textColors[index % textColors.length];

                        return (
                          <div
                            key={todo.todo_id || `todo-${index}`}
                            className="p-3 mb-3 rounded-4 shadow-sm"
                            onClick={() => handleOpenTodo(todo)}
                            style={{
                              backgroundColor: bg,
                              cursor: "pointer",
                              transition: "all 0.25s ease",
                              transform:
                                hoveredTodo === index
                                  ? "scale(1.015)"
                                  : "scale(1)",
                              boxShadow:
                                hoveredTodo === index
                                  ? "0 4px 12px rgba(0,0,0,0.12)"
                                  : "0 1px 4px rgba(0,0,0,0.06)",
                              borderLeft: `4px solid ${color}`,
                            }}
                            onMouseEnter={() => setHoveredTodo(index)}
                            onMouseLeave={() => setHoveredTodo(null)}
                          >
                            {/* Top Row */}
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="pe-2">
                                {/* Title */}
                                <div className="fw-bold text-dark text-truncate d-flex align-items-center gap-1">
                                  <i className="ti ti-check text-muted"></i>
                                  {todo.title}
                                </div>

                                {/* Priority + Status */}
                                <div className="small fw-semibold mt-1 d-flex align-items-center gap-2 flex-wrap">
                                  <span
                                    className="badge px-2 py-1"
                                    style={{
                                      backgroundColor: color,
                                      color: "#fff",
                                      borderRadius: "8px",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    Priority: {todo.priority}
                                  </span>

                                  <span
                                    className="badge bg-white text-dark border fw-semibold"
                                    style={{
                                      borderColor: color,
                                      fontSize: "0.7rem",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    Status: {todo.status}
                                  </span>
                                </div>
                              </div>

                              {/* Assigned By */}
                              <div
                                className="text-end small fw-semibold"
                                style={{ whiteSpace: "nowrap", color }}
                              >
                                👤 {todo.assigned_by_user?.name || "-"}
                              </div>
                            </div>

                            {/* Created Date */}
                            <div className="text-muted small mt-1">
                              <i className="ti ti-calendar-event me-1"></i>
                              {created.format("DD MMM YYYY")}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center text-muted py-5">
                        <i className="ti ti-notes-off fs-2 mb-2" />
                        <p className="mb-0">No recent todos</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Todo Details Modal */}
                {selectedTodo && (
                  <div
                    className={`modal fade ${showTodoModal ? "show d-block" : ""
                      }`}
                    tabIndex={-1}
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    onClick={handleCloseTodo}
                  >
                    <div
                      className="modal-dialog modal-lg modal-dialog-centered"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="modal-content shadow rounded-4 overflow-hidden">
                        <div className="modal-header bg-primary text-white">
                          <h5 className="modal-title">📝 ToDo Details</h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={handleCloseTodo}
                          ></button>
                        </div>

                        <div
                          className="modal-body"
                          style={{ background: "#f8f9fa" }}
                        >
                          <ul className="list-unstyled mb-0">
                            <li className="mb-2">
                              <strong>📌 Title:</strong> {selectedTodo.title}
                            </li>
                            <li className="mb-2">
                              <strong>🧾 Description:</strong>{" "}
                              {selectedTodo.description || "—"}
                            </li>
                            <li className="mb-2">
                              <strong>🎯 Priority:</strong>{" "}
                              {selectedTodo.priority}
                            </li>
                            <li className="mb-2">
                              <strong>📊 Status:</strong> {selectedTodo.status}
                            </li>
                            <li className="mb-2">
                              <strong>📅 Due Date:</strong>{" "}
                              {moment(selectedTodo.due_date).format(
                                "DD MMM YYYY, hh:mm A"
                              )}
                            </li>
                            <li className="mb-2">
                              <strong>👤 Assigned To:</strong>{" "}
                              {selectedTodo.user?.name || "—"}
                            </li>
                            <li className="mb-2">
                              <strong>👤 Assigned By:</strong>{" "}
                              {selectedTodo.assigned_by_user?.name || "—"}
                            </li>
                            <li>
                              <strong>🕒 Created At:</strong>{" "}
                              {moment(selectedTodo.created_at).format(
                                "DD MMM YYYY, hh:mm A"
                              )}
                            </li>
                          </ul>
                        </div>

                        <div className="modal-footer bg-white">
                          <button
                            className="btn btn-secondary"
                            onClick={handleCloseTodo}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="card-footer border-0 d-flex justify-content-end bg-white py-3">
                  <Link
                    to={route.todo}
                    className="btn btn-outline-primary btn-sm rounded-pill"
                  >
                    View All ToDos
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-md-12 d-flex">
              <div className="card flex-fill shadow-sm">
                {/* HEADER */}
                <div className="card-header border-0 pb-0">
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="mb-0 d-flex align-items-center gap-2">
                      <i className="ti ti-address-book me-2" />
                      Recently Added Contacts
                      <Tip text="These are the latest contacts added to your CRM.">
                        <i
                          className="fa fa-info-circle text-primary"
                          style={{ cursor: "pointer", fontSize: "16px" }}
                        />
                      </Tip>
                    </h4>

                    {/* Filter Dropdown */}
                    <div className="dropdown">
                      <Link
                        className="dropdown-toggle"
                        data-bs-toggle="dropdown"
                        to="#"
                        style={{
                          width: "160px",        // ⬅ narrower
                          height: "30px",       // ⬅ shorter
                          fontSize: "12px",     // ⬅ smaller text
                          padding: "2px 6px",   // ⬅ tighter padding
                          lineHeight: "1.2",
                        }}
                      >
                        <i className="ti ti-calendar-check me-2" />
                        Last {filterDays} days
                      </Link>

                      <div className="dropdown-menu dropdown-menu-end">
                        <Link
                          className="dropdown-item"
                          to="#"
                          onClick={() => setFilterDays(7)}
                        >
                          Last 7 days
                        </Link>
                        <Link
                          className="dropdown-item"
                          to="#"
                          onClick={() => setFilterDays(15)}
                        >
                          Last 15 days
                        </Link>
                        <Link
                          className="dropdown-item"
                          to="#"
                          onClick={() => setFilterDays(30)}
                        >
                          Last 30 days
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BODY */}
                <div className="card-body">
                  <div
                    className="table-responsive custom-table"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>
                            <i className="ti ti-user me-1" /> Contact
                          </th>
                          <th>
                            <i className="ti ti-building-bank me-1" /> Company
                          </th>
                          <th>
                            <i className="ti ti-user-star me-1" /> Designation
                          </th>
                          <th>
                            <i className="ti ti-phone me-1" /> Phone
                          </th>
                          <th>
                            <i className="ti ti-mail me-1" /> Email
                          </th>
                          <th>
                            <i className="ti ti-globe me-1" /> Website
                          </th>
                          <th>
                            <i className="ti ti-user-cog me-1" /> Lead Owner
                          </th>
                          <th>
                            <i className="ti ti-calendar-time me-1" /> Created
                            At
                          </th>
                          <th>
                            <i className="ti ti-quote me-1" /> Remark
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {recentContacts.length === 0 ? (
                          <tr>
                            <td
                              colSpan={9}
                              className="text-center text-muted py-5"
                            >
                              <i className="ti ti-user-off fs-2 mb-2 d-block" />

                              <p className="mb-0">No contacts found in last {filterDays} days.</p>
                            </td>
                          </tr>
                        ) : (
                          recentContacts.slice(0, 5).map((item, index) => {
                            const c = item.company;
                            const isHovered = hoveredContactIndex === index;

                            return (
                              <tr
                                key={c.contact_id}
                                onClick={() => handleViewContact(c)}
                                onMouseEnter={() =>
                                  setHoveredContactIndex(index)
                                }
                                onMouseLeave={() =>
                                  setHoveredContactIndex(null)
                                }
                                style={{
                                  backgroundColor: isHovered
                                    ? "#f7f9fc"
                                    : "transparent",
                                  cursor: "pointer",
                                  transform: isHovered
                                    ? "scale(1.005)"
                                    : "scale(1)",
                                  boxShadow: isHovered
                                    ? "0 2px 6px rgba(0,0,0,0.05)"
                                    : "none",
                                  transition:
                                    "transform 0.2s ease, box-shadow 0.2s ease",
                                }}
                              >
                                {/* Contact */}
                                <td>
                                  <strong>
                                    {c.contact_person_name || (
                                      <span className="badge bg-secondary">
                                        N/A
                                      </span>
                                    )}
                                  </strong>
                                </td>

                                {/* Company */}
                                <td>
                                  {c.company_name || (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>

                                {/* Designation */}
                                <td>
                                  {c.contact_person_desigination ? (
                                    <>
                                      <i className="ti ti-award me-1 text-primary" />
                                      {c.contact_person_desigination}
                                    </>
                                  ) : (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>

                                {/* Phone */}
                                <td>
                                  {c.company_phone || (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>

                                {/* Email */}
                                <td>
                                  {c.company_mail || (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>

                                {/* Website */}
                                <td>
                                  {c.website_url ? (
                                    <a
                                      href={c.website_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      🌐 {c.website_url}
                                    </a>
                                  ) : (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>

                                {/* Lead Owner */}
                                <td>{c.username || "Unassigned"}</td>

                                {/* Created At */}
                                <td>
                                  {c.created_at
                                    ? new Date(c.created_at).toLocaleDateString(
                                      "en-IN",
                                      {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      }
                                    )
                                    : "N/A"}
                                </td>

                                {/* Remark */}
                                <td>
                                  {c.contact_remark || (
                                    <span className="text-muted">N/A</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12 d-flex">
              <div className="card flex-fill shadow-sm">
                {/* HEADER */}
                <div className="card-header border-0 pb-0">
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="fw-bold mb-2 d-flex align-items-center gap-2">
                      <i className="ti ti-file-dollar me-1" />
                      Recently Created Deals
                      <Tip text="This section highlights the most recent deals created in the system.">
                        <i
                          className="fa fa-info-circle text-primary"
                          style={{ cursor: "pointer", fontSize: "16px" }}
                        />
                      </Tip>
                    </h4>

                    {/* Filter */}
                    <div className="dropdown">
                      <Link
                        className="dropdown-toggle"
                        data-bs-toggle="dropdown"
                        to="#"
                        style={{
                          width: "160px",        // ⬅ narrower
                          height: "30px",       // ⬅ shorter
                          fontSize: "12px",     // ⬅ smaller text
                          padding: "2px 6px",   // ⬅ tighter padding
                          lineHeight: "1.2",
                        }}
                      >
                        <i className="ti ti-calendar-check me-2" />
                        Last {filterDays} days
                      </Link>

                      <div className="dropdown-menu dropdown-menu-end">
                        <Link
                          className="dropdown-item"
                          to="#"
                          onClick={() => setFilterDays(7)}
                        >
                          Last 7 days
                        </Link>
                        <Link
                          className="dropdown-item"
                          to="#"
                          onClick={() => setFilterDays(15)}
                        >
                          Last 15 days
                        </Link>
                        <Link
                          className="dropdown-item"
                          to="#"
                          onClick={() => setFilterDays(30)}
                        >
                          Last 30 days
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BODY */}
                <div className="card-body">
                  <div
                    className="table-responsive custom-table"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>
                            <i className="ti ti-briefcase me-1" /> Deal Name
                          </th>
                          <th>
                            <i className="ti ti-stairs-up me-1" /> Stage
                          </th>
                          <th>
                            <i className="ti ti-cash me-1" /> Deal Value
                          </th>
                          <th>
                            <i className="ti ti-flag me-1" /> Priority
                          </th>
                          <th>
                            <i className="ti ti-user-star me-1" /> Owner
                          </th>
                          <th>
                            <i className="ti ti-alert-circle me-1" /> Status
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {recentDeals.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-5"
                            >
                              <i className="ti ti-file-off fs-2 mb-2 d-block" />
                              <p className="mb-0">No deals found in last {filterDays} days.</p>
                            </td>
                          </tr>
                        ) : (
                          recentDeals.slice(0, 5).map((deal, index) => {
                            const isHovered = hoveredDealIndex === index;

                            return (
                              <tr
                                key={deal.deal_id}
                                onClick={() => handleViewDeal(deal)}
                                onMouseEnter={() => setHoveredDealIndex(index)}
                                onMouseLeave={() => setHoveredDealIndex(null)}
                                style={{
                                  backgroundColor: isHovered
                                    ? "#f7f9fc"
                                    : "transparent",
                                  cursor: "pointer",
                                  transform: isHovered
                                    ? "scale(1.005)"
                                    : "scale(1)",
                                  boxShadow: isHovered
                                    ? "0 2px 6px rgba(0,0,0,0.05)"
                                    : "none",
                                  transition:
                                    "transform 0.2s ease, box-shadow 0.2s ease",
                                }}
                              >
                                {/* Deal Name */}
                                <td>
                                  <strong>
                                    {deal?.contact?.contact_person_name ||
                                      "N/A"}
                                  </strong>{" "}
                                  ({deal?.contact?.company_name || "N/A"})
                                </td>

                                {/* Stage */}
                                <td>{deal?.status || "N/A"}</td>

                                {/* Deal Value */}
                                <td>
                                  ₹
                                  {Number(
                                    deal?.deal_value || 0
                                  ).toLocaleString()}
                                </td>

                                {/* Priority */}
                                <td>{deal?.deal_priority || "N/A"}</td>

                                {/* Owner */}
                                <td>{deal?.leads?.users?.name || "N/A"}</td>

                                {/* Status Badge */}
                                <td>
                                  <span
                                    className={`badge badge-pill ${["Won", "WON", "won"].includes(
                                      deal?.status
                                    )
                                      ? "bg-success"
                                      : ["Lost", "LOST", "lost"].includes(
                                        deal?.status
                                      )
                                        ? "bg-danger"
                                        : "bg-warning text-dark"
                                      }`}
                                  >
                                    {deal?.status || "N/A"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12 d-flex">
              <div className="card w-100" id="revenue-forecast-card">
                <div style={{ overflow: "visible" }} className="card-header border-0 pb-0">
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="d-flex align-items-center gap-2">
                      <i className="ti ti-trending-up me-1"></i>
                      Revenue Forecast
                      <Tip text="This chart displays the number of deals won or created each year.">
                        <i
                          className="fa fa-info-circle text-primary"
                          style={{ cursor: "pointer", fontSize: "16px" }}
                        />
                      </Tip>
                    </h4>
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <div className="d-flex gap-1 flex-shrink-0 align-items-center me-3">
                        <ChartExportControl
                          variant="dots"
                          data={derivedDealsByYearOptions?.series ?? []}
                          filename="Revenue_Forecast"
                          cardId="revenue-forecast-card"
                        />
                      </div>
                      <div className="dropdown">
                        <Link
                          className="dropdown-toggle"
                          data-bs-toggle="dropdown"
                          to="#"
                          style={{
                            width: "160px",        // ⬅ narrower
                            height: "30px",       // ⬅ shorter
                            fontSize: "12px",     // ⬅ smaller text
                            padding: "2px 6px",   // ⬅ tighter padding
                            lineHeight: "1.2",
                          }}
                        >
                          {selectedRange === "3m"
                            ? "Last 3 months"
                            : selectedRange === "6m"
                              ? "Last 6 months"
                              : "Last 12 months"}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link
                            to="#"
                            className="dropdown-item"
                            onClick={() => setSelectedRange("3m")}
                          >
                            Last 3 months
                          </Link>
                          <Link
                            to="#"
                            className="dropdown-item"
                            onClick={() => setSelectedRange("6m")}
                          >
                            Last 6 months
                          </Link>
                          <Link
                            to="#"
                            className="dropdown-item"
                            onClick={() => setSelectedRange("12m")}
                          >
                            Last 12 months
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div id="deals-year" ref={dealsByYearRef}>
                    {hasDealsByYearData ? (
                      <Suspense fallback={<Loader />}>
                        <ReactApexChart
                          options={derivedDealsByYearOptions.options}
                          series={derivedDealsByYearOptions.series}
                          type="line"
                          height={300}
                        />
                      </Suspense>
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center"
                        style={{ height: 300 }}>
                        <i className="ti ti-chart-bar-off fs-2 text-muted mb-2" />
                        <p className="mb-0 text-muted fw-semibold">
                          No revenue data available for the selected period
                        </p>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showScrollTop && (
        <button
          className="scroll-to-top-btn shadow"
          onClick={scrollToTop}
          title="Back to top"
        >
          <span style={{ fontSize: "20px", fontWeight: "bold" }}>↑</span>
        </button>
      )}
    </>
  );
};

export default DealsDashboard;
